import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiCamera,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiMapPin,
  FiSearch,
  FiSmartphone,
  FiUpload,
  FiUser,
  FiZap,
} from "react-icons/fi";
import {
  normalizeEmailInput,
  validateEmail,
} from "../utils/emailValidation";
import { checkCustomerByPan } from "../utils/panCustomerCheck";
import { parseAadhaarSendOtpResponse, parseAadhaarVerifyResponse } from "../utils/aadhaarVerifyUtils";
import {
  getAadhaarNumberSecure,
  getAadhaarReferenceSecure,
  sanitizeAadhaarProfile,
  setAadhaarNumberSecure,
  setAadhaarReferenceSecure,
} from "../utils/applyFlowStorage";
import { fundswallaOtpHeaders } from "../config/applyLoanOtp";
import {
  backendStepForScreen,
  buildSessionFromStored,
  clearCustomerSession,
  fetchCustomerSession,
  getCustomerSession,
  mergeCustomerSession,
  pickFurthestScreen,
  pickResumeScreen,
  postCustomerSession,
  resolveResumeScreen,
  saveCustomerSession,
} from "../utils/applyFlowSession";
import { loanBackendUrl, loanBackendPostUrl, bankAcVerificationUrl, taskApiPostUrl } from "../config/taskApi";
import {
  buildBankVerificationPayload,
  getBankVerificationBankingId,
  getBankVerificationMessage,
  isBankVerificationSuccess,
} from "../utils/bankAcVerification";
import { fetchCustomerProfile } from "../utils/profileCustomer";
import {
  buildLeadApplicationPayload,
  submitLeadApplication,
} from "../utils/leadApplication";
import {
  ALL_BANKS,
  BANK_ACCOUNTS,
  POPULAR_BANKS,
} from "./applyNewFlowBankData";
import indiaEmblem from "../assets/images/india-emblem.png";
import aadhaarLogoMark from "../assets/images/aadhaar-logo-mark.png";
import aadhaarLogoRight from "../assets/images/aadhaar-logo-right.png";
import OfferHeroArt from "./OfferHeroArt";
import FwSelfieCamera from "./FwSelfieCamera";
import FwProfileScreen from "./FwProfileScreen";
import FwPaymentHistoryScreen from "./FwPaymentHistoryScreen";
import "./ApplyNewFlow.css";

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const OTP_RESEND_SECONDS = 30;
const LOAN_PURPOSES = [
  "Personal Use",
  "Medical Emergency",
  "Education",
  "Wedding",
  "Travel",
  "Home Renovation",
  "Other",
];
const EMPLOYMENT_TYPES = ["Salaried", "Self Employed"];
const SALARY_MODES = ["Bank Transfer", "Cheque", "Cash"];
const REFERENCE_RELATIONS = [
  "Mother",
  "Father",
  "Brother",
  "Sister",
  "Spouse",
  "Friend",
  "Colleague",
  "Other",
];
const VERIFY_STEPS = [
  "Checking your details...",
  "Verifying employment...",
  "Reviewing loan eligibility...",
];
const VERIFY_PAN_URL = "https://fundswalla.in/loan_backend/verifyPan.php";
const SEND_AADHAAR_OTP_URL = "https://fundswalla.in/loan_backend/sendAadhaarOtp.php";
const VERIFY_AADHAAR_OTP_URL = "https://fundswalla.in/loan_backend/verifyAadhaarOtp.php";
const SEND_OTP_URL = taskApiPostUrl("sendOtp");
const VERIFY_OTP_URL = taskApiPostUrl("verifyOtp");
const LOGIN_CUSTOMER_URL = loanBackendPostUrl("loginCustomerMobile.php");
const PROFILE_CUSTOMER_URL = loanBackendUrl("profileCustomer.php");
const LEAD_APPLICATION_URL = loanBackendPostUrl("leadApplication.php");
const BANK_AC_VERIFY_URL = bankAcVerificationUrl();
const LOCATION_STORAGE_KEY = "applyFlowLocation";
const SELFIE_STORAGE_KEY = "applyFlowSelfiePhoto";

const EMPTY_LOCATION = { latitude: "", longitude: "", coordinates: "" };

const readStoredLocation = () => {
  try {
    const raw =
      localStorage.getItem(LOCATION_STORAGE_KEY) ||
      sessionStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) return EMPTY_LOCATION;
    const parsed = JSON.parse(raw);
    return {
      latitude: String(parsed.latitude || ""),
      longitude: String(parsed.longitude || ""),
      coordinates: String(parsed.coordinates || ""),
    };
  } catch {
    return EMPTY_LOCATION;
  }
};

const persistLocation = (coords) => {
  const serialized = JSON.stringify(coords);
  localStorage.setItem(LOCATION_STORAGE_KEY, serialized);
  sessionStorage.setItem(LOCATION_STORAGE_KEY, serialized);
};

const readStoredSelfie = () => {
  try {
    return sessionStorage.getItem(SELFIE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const persistSelfie = (photo) => {
  try {
    if (photo && String(photo).startsWith("data:")) {
      sessionStorage.setItem(SELFIE_STORAGE_KEY, photo);
    }
  } catch {
    /* quota — skip persist */
  }
};

const parseStoredJson = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const formatMobileDisplay = (mobile) => {
  const digits = String(mobile || "").replace(/\D/g, "").slice(-10);
  if (digits.length !== 10) return "+91";
  return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
};

const formatDob = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
};

const formatCountdown = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

const formatLoanAmount = (value) => {
  const amount = Number(String(value || "").replace(/\D/g, ""));
  if (!amount) return "₹25,000";
  return `₹${amount.toLocaleString("en-IN")}`;
};

const FwDropdown = ({
  id,
  label,
  value,
  placeholder,
  options,
  openKey,
  setOpenKey,
  onChange,
}) => {
  const rootRef = useRef(null);
  const isOpen = openKey === id;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpenKey(null);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, setOpenKey]);

  return (
    <div className="fw-field" ref={rootRef}>
      <span>{label}</span>
      <div className={`fw-dropdown ${isOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className={`fw-dropdown__trigger ${!value ? "is-placeholder" : ""}`}
          aria-expanded={isOpen}
          onClick={() => setOpenKey(isOpen ? null : id)}
        >
          <span>{value || placeholder}</span>
          <FiChevronDown />
        </button>
        {isOpen && (
          <div className="fw-dropdown__panel">
            <ul className="fw-dropdown__menu" role="listbox">
              {options.map((item) => (
                <li key={item}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === item}
                    className={value === item ? "is-active" : ""}
                    onClick={() => {
                      onChange(item);
                      setOpenKey(null);
                    }}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const WEEK_DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getMonthInfo = (offset = 0) => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
};

const formatSalaryDate = (day, month, year) => {
  const dd = String(day).padStart(2, "0");
  const mm = String(month + 1).padStart(2, "0");
  const yy = String(year).slice(-2);
  return `${dd}${mm}${yy}`;
};

const formatSalaryDateDisplay = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length !== 6) return raw || "";
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 6)}`;
};

const isValidSalaryDate = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!/^\d{6}$/.test(digits)) return false;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = 2000 + Number(digits.slice(4, 6));
  if (month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return false;

  const selected = new Date(year, month - 1, day);
  selected.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const current = getMonthInfo(0);
  const next = getMonthInfo(1);
  const isCurrent = year === current.year && month === current.month + 1;
  const isNext = year === next.year && month === next.month + 1;
  if (!isCurrent && !isNext) return false;
  if (selected < today) return false;
  return true;
};

const FwDayCalendar = ({
  id,
  label,
  value,
  placeholder,
  openKey,
  setOpenKey,
  onChange,
}) => {
  const rootRef = useRef(null);
  const isOpen = openKey === id;
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current, 1 = next
  const { year, month } = getMonthInfo(monthOffset);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = new Date(year, month, 1).getDay();
  const blanks = Array.from({ length: startWeekday }, (_, i) => `b-${i}`);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpenKey(null);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, setOpenKey]);

  useEffect(() => {
    if (isOpen) setMonthOffset(0);
  }, [isOpen]);

  return (
    <div className="fw-field" ref={rootRef}>
      <span>{label}</span>
      <div className={`fw-daycal ${isOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className={`fw-daycal__trigger ${!value ? "is-placeholder" : ""}`}
          aria-expanded={isOpen}
          onClick={() => setOpenKey(isOpen ? null : id)}
        >
          <span>{value ? formatSalaryDateDisplay(value) : placeholder}</span>
          <FiCalendar />
        </button>
        {isOpen && (
          <div className="fw-daycal__panel" role="listbox" aria-label="Select salary date">
            <div className="fw-daycal__head">
              <button
                type="button"
                className="fw-daycal__nav"
                aria-label="Previous month"
                disabled={monthOffset <= 0}
                onClick={() => setMonthOffset(0)}
              >
                <FiChevronRight style={{ transform: "rotate(180deg)" }} />
              </button>
              <strong>
                {MONTH_NAMES[month]} {year}
              </strong>
              <button
                type="button"
                className="fw-daycal__nav"
                aria-label="Next month"
                disabled={monthOffset >= 1}
                onClick={() => setMonthOffset(1)}
              >
                <FiChevronRight />
              </button>
            </div>
            <div className="fw-daycal__week">
              {WEEK_DAYS.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>
            <div className="fw-daycal__grid">
              {blanks.map((key) => (
                <span key={key} className="fw-daycal__blank" />
              ))}
              {days.map((day) => {
                const formatted = formatSalaryDate(day, month, year);
                const cellDate = new Date(year, month, day);
                cellDate.setHours(0, 0, 0, 0);
                const isPast = monthOffset === 0 && cellDate < today;
                return (
                  <button
                    key={`${month}-${day}`}
                    type="button"
                    role="option"
                    aria-selected={value === formatted}
                    disabled={isPast}
                    className={`fw-daycal__day ${value === formatted ? "is-active" : ""} ${isPast ? "is-disabled" : ""}`}
                    onClick={() => {
                      if (isPast) return;
                      onChange(formatted);
                      setOpenKey(null);
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <p className="fw-daycal__hint">DDMMYY · From today onwards</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ApplyNewFlow = () => {
  const [sessionBooting, setSessionBooting] = useState(true);
  const [screen, setScreen] = useState("landing");
  const [sheet, setSheet] = useState(null);
  const [sheetVisible, setSheetVisible] = useState(false);

  const [mobile, setMobile] = useState("");
  const [consent, setConsent] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  const [locationError, setLocationError] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationCoords, setLocationCoords] = useState(readStoredLocation);
  const leadPayloadRef = useRef({});

  const [pan, setPan] = useState("");
  const [email, setEmail] = useState("");
  const [panError, setPanError] = useState("");
  const [panStatus, setPanStatus] = useState("");
  const [emailError, setEmailError] = useState("");
  const [checking, setChecking] = useState(false);
  const [panChecking, setPanChecking] = useState(false);
  const [panProfile, setPanProfile] = useState(null);
  const panCheckLockRef = useRef(false);
  const lastFetchedPanRef = useRef("");

  const [holdMessage, setHoldMessage] = useState("");
  const [employment, setEmployment] = useState({
    employer: "",
    salary: "",
    salaryDate: "",
    loanAmount: "",
    loanPurpose: "",
    employmentType: "",
    salaryMode: "",
  });
  const [employmentError, setEmploymentError] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [verifyStep, setVerifyStep] = useState(0);
  const [offerSeconds, setOfferSeconds] = useState(541);
  const [selectedBankId, setSelectedBankId] = useState("hdfc");
  const [bankSearch, setBankSearch] = useState("");
  const [aaMobile, setAaMobile] = useState("");
  const [aaMobileError, setAaMobileError] = useState("");
  const [aaOtpDigits, setAaOtpDigits] = useState(["", "", "", "", "", ""]);
  const [aaOtpError, setAaOtpError] = useState("");
  const [aaResendTimer, setAaResendTimer] = useState(0);
  const [selectedAccountId, setSelectedAccountId] = useState("acc1");
  const [bankAccounts, setBankAccounts] = useState(BANK_ACCOUNTS);
  const [addBankSource, setAddBankSource] = useState("offer");
  const aaOtpRefs = useRef([]);
  const [activeReferenceTab, setActiveReferenceTab] = useState(0);
  const [references, setReferences] = useState([
    { name: "", mobile: "", relation: "" },
    { name: "", mobile: "", relation: "" },
  ]);
  const [referenceError, setReferenceError] = useState("");
  const [selfieFaceReady, setSelfieFaceReady] = useState(false);
  const [selfieHint, setSelfieHint] = useState(
    "Position your face inside the frame and ensure that the circle is green."
  );
  const [selfieError, setSelfieError] = useState("");
  const [selfiePhoto, setSelfiePhoto] = useState(readStoredSelfie);
  const selfieCameraRef = useRef(null);
  const [paymentHistoryTab, setPaymentHistoryTab] = useState("transactions");
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [aadhaarError, setAadhaarError] = useState("");
  const [aadhaarStatus, setAadhaarStatus] = useState("");
  const [aadhaarProfile, setAadhaarProfile] = useState(null);
  const [aadhaarConsent, setAadhaarConsent] = useState(false);
  const [aadhaarSending, setAadhaarSending] = useState(false);
  const aadhaarCheckLockRef = useRef(false);
  const [aadhaarOtpDigits, setAadhaarOtpDigits] = useState(["", "", "", "", "", ""]);
  const [aadhaarOtpError, setAadhaarOtpError] = useState("");
  const [aadhaarOtpVerifying, setAadhaarOtpVerifying] = useState(false);
  const [aadhaarResendTimer, setAadhaarResendTimer] = useState(0);
  const aadhaarOtpRefs = useRef([]);
  const [disburseBank, setDisburseBank] = useState({
    bankName: "",
    holderName: "",
    accountNumber: "",
    ifsc: "",
  });
  const [disburseBankError, setDisburseBankError] = useState("");
  const [disburseBankSuccess, setDisburseBankSuccess] = useState("");
  const [disburseBankVerifying, setDisburseBankVerifying] = useState(false);
  const [customerBankingId, setCustomerBankingId] = useState("");
  const [customerLeadId, setCustomerLeadId] = useState("");
  const [customerStep, setCustomerStep] = useState(1);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitError, setLeadSubmitError] = useState("");
  const leadSubmittedRef = useRef(false);

  const restoreFromLoginSession = (login) => {
    if (!login) return "location";

    const fields = login.savedFields || {};
    const session = login.sessionData || {};
    const pick = (...keys) => {
      for (const key of keys) {
        const value = fields[key] ?? session[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          return value;
        }
      }
      return "";
    };

    const restoredMobile = String(login.customerMobile || pick("customer_mobile", "mobile") || "")
      .replace(/\D/g, "")
      .slice(-10);
    if (restoredMobile.length === 10) setMobile(restoredMobile);

    const restoredPan = String(pick("pan", "pan_number", "pancard") || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10);
    if (restoredPan) setPan(restoredPan);

    const restoredEmail = String(pick("email", "email_personal", "email_id") || "");
    if (restoredEmail) setEmail(restoredEmail);

    const employer = String(pick("employer", "employer_name", "company_name") || "");
    const salary = String(pick("salary", "monthly_income", "monthlyIncome") || "");
    const salaryDate = String(pick("salary_date", "salaryDate") || "");
    const loanAmount = String(pick("loan_amount", "loanAmount") || "");
    const loanPurpose = String(pick("loan_purpose", "loanPurpose") || "");
    const employmentType = String(pick("employment_type", "employmentType") || "");
    const salaryMode = String(pick("salary_mode", "salaryMode") || "");
    if (
      employer ||
      salary ||
      salaryDate ||
      loanAmount ||
      loanPurpose ||
      employmentType ||
      salaryMode
    ) {
      setEmployment((prev) => ({
        ...prev,
        ...(employer ? { employer } : {}),
        ...(salary ? { salary } : {}),
        ...(salaryDate ? { salaryDate } : {}),
        ...(loanAmount ? { loanAmount } : {}),
        ...(loanPurpose ? { loanPurpose } : {}),
        ...(employmentType ? { employmentType } : {}),
        ...(salaryMode ? { salaryMode } : {}),
      }));
    }

    const aadhaar = String(pick("aadhaar_number", "aadhaar", "aadhar_no") || "").replace(/\D/g, "");
    if (aadhaar.length === 12) {
      setAadhaarNumber(aadhaar);
      setAadhaarNumberSecure(aadhaar);
    }

    const storedAadhaarProfile = parseStoredJson(pick("aadhaar_profile"));
    if (storedAadhaarProfile && typeof storedAadhaarProfile === "object") {
      setAadhaarProfile(storedAadhaarProfile);
      setAadhaarConsent(true);
    }

    const storedPanProfile = parseStoredJson(pick("pan_profile"));
    if (storedPanProfile && typeof storedPanProfile === "object") {
      setPanProfile(storedPanProfile);
    }

    const storedBank = parseStoredJson(pick("disburse_bank"));
    if (storedBank && typeof storedBank === "object") {
      setDisburseBank((prev) => ({ ...prev, ...storedBank }));
    }
    const bankingId = String(pick("cust_banking_id") || "");
    if (bankingId) setCustomerBankingId(bankingId);

    const storedRefs = parseStoredJson(pick("references"));
    if (Array.isArray(storedRefs) && storedRefs.length) {
      setReferences((prev) =>
        prev.map((item, index) => ({
          ...item,
          ...(storedRefs[index] || {}),
        }))
      );
    }

    const lat = String(pick("latitude") || "");
    const lng = String(pick("longitude") || "");
    const coordinates = String(pick("coordinates") || (lat && lng ? `${lat},${lng}` : ""));
    if (lat || lng || coordinates) {
      const next = { latitude: lat, longitude: lng, coordinates };
      setLocationCoords(next);
      persistLocation(next);
    }

    if (login.leadId) setCustomerLeadId(String(login.leadId));
    const step = Math.min(Math.max(Number(login.step) || 1, 1), 3);
    const apiScreen = login.screen || resolveResumeScreen(step, login.savedFields);
    const stored = getCustomerSession();
    const localLast = pickFurthestScreen(
      stored?.last_screen,
      fields.last_screen || login.sessionData?.last_screen
    );
    const targetScreen = pickFurthestScreen(localLast, apiScreen);
    setCustomerStep(step);
    saveCustomerSession({
      ...login.sessionData,
      step,
      last_screen: targetScreen,
      customer_mobile: restoredMobile || login.customerMobile,
      saved_fields: {
        ...(stored?.saved_fields || {}),
        ...(login.savedFields || {}),
        last_screen: targetScreen,
        ...(fields.aadhaar_verified === "1" ||
        fields.aadhaar_verified === true ||
        stored?.saved_fields?.aadhaar_verified === "1" ||
        aadhaarProfile
          ? { aadhaar_verified: "1" }
          : {}),
        ...(aadhaar || fields.aadhaar_number ? { aadhaar_number: aadhaar || fields.aadhaar_number } : {}),
      },
    });

    return targetScreen;
  };

  const buildProgressFields = (extra = {}) => ({
    pan: pan.trim().toUpperCase(),
    email: normalizeEmailInput(email),
    latitude: locationCoords.latitude,
    longitude: locationCoords.longitude,
    coordinates: locationCoords.coordinates,
    employer: employment.employer,
    monthly_income: employment.salary,
    salary_date: employment.salaryDate,
    loan_amount: employment.loanAmount,
    loan_purpose: employment.loanPurpose,
    employment_type: employment.employmentType,
    salary_mode: employment.salaryMode,
    ...extra,
  });

  const saveCustomerProgress = async (step, extraFields = {}) => {
    const customerMobile = String(mobile || getCustomerSession()?.customer_mobile || "")
      .replace(/\D/g, "")
      .slice(-10);
    if (customerMobile.length !== 10) return null;

    try {
      const login = await postCustomerSession(
        LOGIN_CUSTOMER_URL,
        customerMobile,
        step,
        buildProgressFields(extraFields)
      );
      if (login.ok) {
        restoreFromLoginSession(login);
      }
      return login;
    } catch {
      return null;
    }
  };

  const loginCustomerByMobile = async (mobileValue) => {
    const customerMobile = String(mobileValue || "").replace(/\D/g, "").slice(-10);
    if (customerMobile.length !== 10) {
      return { ok: false, screen: "location" };
    }

    const login = await fetchCustomerSession(LOGIN_CUSTOMER_URL, customerMobile);
    if (!login.ok) {
      return { ok: false, screen: "location", message: login.message };
    }

    const nextScreen = restoreFromLoginSession(login);
    return { ok: true, screen: nextScreen, login };
  };

  useEffect(() => {
    const stored = getCustomerSession();
    const storedLogin = buildSessionFromStored(stored);
    if (!storedLogin) {
      setSessionBooting(false);
      return undefined;
    }

    // Local restore only — an API call on mount can reset the session
    restoreFromLoginSession(storedLogin);
    setScreen(storedLogin.screen);
    setSessionBooting(false);

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionBooting || screen === "landing") return;

    const customerMobile = String(mobile || getCustomerSession()?.customer_mobile || "")
      .replace(/\D/g, "")
      .slice(-10);
    if (customerMobile.length !== 10) return;

    const step = backendStepForScreen(screen) || customerStep;
    mergeCustomerSession({
      customer_mobile: customerMobile,
      step,
      last_screen: screen,
      saved_fields: {
        ...(getCustomerSession()?.saved_fields || {}),
        pan: pan.trim().toUpperCase(),
        email: normalizeEmailInput(email),
        latitude: locationCoords.latitude,
        longitude: locationCoords.longitude,
        coordinates: locationCoords.coordinates,
        employer: employment.employer,
        monthly_income: employment.salary,
        salary_date: employment.salaryDate,
        loan_amount: employment.loanAmount,
        loan_purpose: employment.loanPurpose,
        employment_type: employment.employmentType,
        salary_mode: employment.salaryMode,
        ...(aadhaarNumber ? { aadhaar_number: aadhaarNumber } : {}),
        ...(aadhaarProfile
          ? { aadhaar_verified: "1", aadhaar_profile: aadhaarProfile }
          : {}),
        ...(panProfile ? { pan_profile: panProfile } : {}),
        disburse_bank: disburseBank,
        ...(customerBankingId ? { cust_banking_id: customerBankingId } : {}),
        references,
        last_screen: screen,
      },
    });
    if (step) setCustomerStep(step);
  }, [
    screen,
    sessionBooting,
    mobile,
    customerStep,
    aadhaarNumber,
    aadhaarProfile,
    pan,
    email,
    locationCoords,
    employment,
    panProfile,
    disburseBank,
    customerBankingId,
    references,
  ]);

  useEffect(() => {
    if (screen !== "profile") return undefined;

    const stored = getCustomerSession() || {};
    const fields = stored.saved_fields || {};
    const customerMobile = String(
      mobile || stored.customer_mobile || fields.customer_mobile || ""
    )
      .replace(/\D/g, "")
      .slice(-10);

    const storedPan = parseStoredJson(fields.pan_profile);
    const storedAadhaar = parseStoredJson(fields.aadhaar_profile);
    const localName =
      panProfile?.name ||
      storedPan?.name ||
      aadhaarProfile?.name ||
      storedAadhaar?.name ||
      "";
    const localEmail = email || fields.email || fields.email_personal || "";
    const localAvatar = selfiePhoto || readStoredSelfie() || "";

    if (localName || localEmail || customerMobile) {
      setCustomerProfile((prev) => ({
        ok: true,
        name: prev?.name || localName,
        email: prev?.email || localEmail,
        mobile: prev?.mobile || customerMobile,
        avatarUrl: prev?.avatarUrl || localAvatar,
        raw: prev?.raw || {},
      }));
    }

    if (customerMobile.length !== 10) {
      setProfileLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProfileLoading(true);

    fetchCustomerProfile(PROFILE_CUSTOMER_URL, customerMobile)
      .then((profile) => {
        if (cancelled || !profile.ok) return;
        setCustomerProfile((prev) => ({
          ...prev,
          ...profile,
          name: profile.name || prev?.name || localName,
          email: profile.email || prev?.email || localEmail,
          mobile: profile.mobile || prev?.mobile || customerMobile,
          avatarUrl: localAvatar || profile.avatarUrl || prev?.avatarUrl || "",
        }));
        if (profile.email) setEmail(profile.email);
        if (profile.mobile) setMobile(profile.mobile);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [screen, mobile]);

  useEffect(() => {
    document.body.classList.add("fw-apply-open");
    return () => document.body.classList.remove("fw-apply-open");
  }, []);

  useEffect(() => {
    if (!sheet) {
      setSheetVisible(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setSheetVisible(true));
    return () => cancelAnimationFrame(id);
  }, [sheet]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timer = setInterval(() => {
      setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => {
    if (
      screen !== "employment" &&
      screen !== "referenceDetails" &&
      screen !== "addBankDetails"
    ) {
      setOpenDropdown(null);
    }
  }, [screen]);

  useEffect(() => {
    if (screen !== "processing") {
      setVerifyStep(0);
      return undefined;
    }

    const stepTimer = setInterval(() => {
      setVerifyStep((prev) => (prev + 1) % VERIFY_STEPS.length);
    }, 1000);

    const doneTimer = setTimeout(() => {
      setScreen("approved");
    }, 3200);

    return () => {
      clearInterval(stepTimer);
      clearTimeout(doneTimer);
    };
  }, [screen]);

  useEffect(() => {
    if (screen !== "offer") {
      setOfferSeconds(541);
      return undefined;
    }

    const timer = setInterval(() => {
      setOfferSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [screen]);

  useEffect(() => {
    if (aaResendTimer <= 0) return undefined;
    const timer = setInterval(() => {
      setAaResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [aaResendTimer]);

  useEffect(() => {
    if (aadhaarResendTimer <= 0) return undefined;
    const timer = setInterval(() => {
      setAadhaarResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [aadhaarResendTimer]);

  const getBank = (bankId) =>
    ALL_BANKS.find((bank) => bank.id === bankId) || POPULAR_BANKS[1];

  const filteredBanks = ALL_BANKS.filter((bank) =>
    bank.name.toLowerCase().includes(bankSearch.trim().toLowerCase())
  );

  const openAaOtpSheet = () => {
    if (!MOBILE_REGEX.test(aaMobile)) {
      setAaMobileError("Enter a valid 10-digit mobile number");
      return;
    }
    setAaMobileError("");
    setAaOtpDigits(["", "", "", "", "", ""]);
    setAaOtpError("");
    setAaResendTimer(OTP_RESEND_SECONDS);
    openSheet("aaOtp");
  };

  const verifyAaOtp = () => {
    const value = aaOtpDigits.join("");
    if (value.length !== 6) {
      setAaOtpError("Enter the 6-digit OTP");
      return;
    }
    closeSheet();
    setTimeout(() => setScreen("aaAccount"), 280);
  };

  const handleAaOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setAaOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setAaOtpError("");
    if (digit && index < 5) aaOtpRefs.current[index + 1]?.focus();
  };

  const handleAaOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !aaOtpDigits[index] && index > 0) {
      aaOtpRefs.current[index - 1]?.focus();
    }
  };

  const updateReference = (index, field, value) => {
    setReferences((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setReferenceError("");
  };

  const normalizeRefName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const getApplicantMobile = () =>
    String(mobile || getCustomerSession()?.customer_mobile || "")
      .replace(/\D/g, "")
      .slice(-10);

  const validateReference = (ref) => {
    if (!ref.name.trim()) return "Enter reference name";
    if (!MOBILE_REGEX.test(ref.mobile)) return "Enter a valid 10-digit mobile number";
    if (!ref.relation) return "Select relation";
    return "";
  };

  const getReferenceUniquenessError = (refs, index) => {
    const current = refs[index] || {};
    const other = refs[index === 0 ? 1 : 0] || {};
    const currentMobile = String(current.mobile || "").replace(/\D/g, "").slice(-10);
    const otherMobile = String(other.mobile || "").replace(/\D/g, "").slice(-10);
    const applicantMobile = getApplicantMobile();

    if (currentMobile.length === 10 && currentMobile === applicantMobile) {
      return "Reference mobile cannot be the same as your mobile number";
    }
    if (
      currentMobile.length === 10 &&
      otherMobile.length === 10 &&
      currentMobile === otherMobile
    ) {
      return "Both references must have different mobile numbers";
    }

    const currentName = normalizeRefName(current.name);
    const otherName = normalizeRefName(other.name);
    if (currentName && otherName && currentName === otherName) {
      return "Both references must have different names";
    }

    return "";
  };

  const proceedReference = () => {
    const currentError = validateReference(references[activeReferenceTab]);
    if (currentError) {
      setReferenceError(currentError);
      return;
    }

    const uniquenessError = getReferenceUniquenessError(
      references,
      activeReferenceTab
    );
    if (uniquenessError) {
      setReferenceError(uniquenessError);
      return;
    }

    if (activeReferenceTab === 0) {
      setActiveReferenceTab(1);
      setReferenceError("");
      return;
    }

    const firstError = validateReference(references[0]);
    if (firstError) {
      setActiveReferenceTab(0);
      setReferenceError(firstError);
      return;
    }

    const firstUniqueError = getReferenceUniquenessError(references, 0);
    if (firstUniqueError) {
      setActiveReferenceTab(0);
      setReferenceError(firstUniqueError);
      return;
    }

    setReferenceError("");
    setScreen("referenceReview");
  };

  const confirmReferences = () => {
    const firstError = validateReference(references[0]);
    if (firstError) {
      setActiveReferenceTab(0);
      setReferenceError(firstError);
      setScreen("referenceDetails");
      return;
    }
    const secondError = validateReference(references[1]);
    if (secondError) {
      setActiveReferenceTab(1);
      setReferenceError(secondError);
      setScreen("referenceDetails");
      return;
    }
    const uniqueError =
      getReferenceUniquenessError(references, 0) ||
      getReferenceUniquenessError(references, 1);
    if (uniqueError) {
      setActiveReferenceTab(1);
      setReferenceError(uniqueError);
      setScreen("referenceDetails");
      return;
    }

    setSelfieFaceReady(false);
    setSelfieError("");
    setSelfieHint("Position your face inside the frame and ensure that the circle is green.");
    setScreen("selfieIntro");
  };

  const formatAadhaarDisplay = (digits) =>
    String(digits || "")
      .replace(/\D/g, "")
      .slice(0, 12)
      .replace(/(\d{4})(?=\d)/g, "$1 ")
      .trim();

  const getAadhaarApiMessage = (data) =>
    data?.message || data?.responseMessage || data?.error || "";

  const mapAadhaarProfile = (profile, number) => {
    const safe = sanitizeAadhaarProfile(profile) || {};
    const name =
      safe.name ||
      safe.fullName ||
      [safe.firstName, safe.middleName, safe.lastName].filter(Boolean).join(" ") ||
      "—";
    return {
      name,
      dob: formatDob(safe.dob || safe.dateOfBirth || safe.date_of_birth),
      dobRaw: safe.dob || safe.dateOfBirth || safe.date_of_birth || "",
      gender: safe.gender || safe.sex || "",
      address: safe.address || safe.careOf || "",
      aadhaar: String(number || "").replace(/\D/g, "").slice(0, 12),
      raw: safe,
    };
  };

  const sendAadhaarOtp = async (rawAadhaar, options = {}) => {
    if (rawAadhaar && typeof rawAadhaar === "object") {
      rawAadhaar = undefined;
    }
    const { requireConsent = true, openOtpSheet = true } = options;
    const number = String(
      rawAadhaar || getAadhaarNumberSecure() || aadhaarNumber
    )
      .replace(/\D/g, "")
      .slice(0, 12);
    const showOnOtpSheet = openOtpSheet || sheet === "aadhaarOtp";
    const setSendError = (msg) => {
      if (showOnOtpSheet) {
        setAadhaarOtpError(msg);
        return;
      }
      setAadhaarError(msg);
      setAadhaarStatus("");
    };

    if (!/^\d{12}$/.test(number)) {
      setSendError("Enter a valid 12-digit Aadhaar number");
      return;
    }
    if (requireConsent && !aadhaarConsent) {
      setAadhaarError("Please accept Aadhaar verification consent");
      setAadhaarStatus("");
      return;
    }
    if (aadhaarCheckLockRef.current) return;

    aadhaarCheckLockRef.current = true;
    setAadhaarSending(true);
    setAadhaarError("");
    setAadhaarStatus("");
    setAadhaarOtpError("");

    try {
      const res = await axios.post(
        SEND_AADHAAR_OTP_URL,
        { aadhaar_number: number },
        { headers: { "Content-Type": "application/json" }, timeout: 45000 }
      );

      const responseData = res.data || {};
      const parsedSend = parseAadhaarSendOtpResponse(responseData);
      const apiMessage =
        getAadhaarApiMessage(responseData) ||
        parsedSend.responseMessage ||
        "Unable to send Aadhaar OTP. Please try again.";

      if (parsedSend.success === false) {
        setSendError(apiMessage);
        return;
      }

      if (
        parsedSend.responseMessage === "Aadhar does not exist" ||
        parsedSend.responseCode === "EAE168"
      ) {
        setSendError("Invalid Aadhaar number.");
        return;
      }

      if (!parsedSend.isSuccess) {
        setSendError(apiMessage);
        return;
      }

      setAadhaarNumberSecure(number);
      setAadhaarReferenceSecure(parsedSend.referenceId || getAadhaarReferenceSecure() || "");
      setAadhaarOtpDigits(["", "", "", "", "", ""]);

      const waitMatch = String(apiMessage).match(/(\d+)\s*(second|sec)/i);
      const waitSeconds = waitMatch ? Number(waitMatch[1]) : OTP_RESEND_SECONDS;
      setAadhaarResendTimer(
        Number.isFinite(waitSeconds) && waitSeconds > 0
          ? waitSeconds
          : OTP_RESEND_SECONDS
      );

      if (openOtpSheet) {
        setAadhaarStatus(apiMessage || "OTP sent successfully.");
        if (showOnOtpSheet && sheet === "aadhaarOtp") {
          setAadhaarOtpError("");
        }
        openSheet("aadhaarOtp");
        setTimeout(() => aadhaarOtpRefs.current[0]?.focus(), 320);
        return;
      }

      setAadhaarStatus(
        apiMessage || "Aadhaar verified. Accept consent and click Proceed."
      );
    } catch (err) {
      setSendError(
        getAadhaarApiMessage(err?.response?.data) ||
          "OTP failed. Please try again."
      );
    } finally {
      aadhaarCheckLockRef.current = false;
      setAadhaarSending(false);
    }
  };

  const resendAadhaarOtp = () => {
    sendAadhaarOtp(getAadhaarNumberSecure() || aadhaarNumber, {
      requireConsent: false,
      openOtpSheet: true,
    });
  };

  const checkAadhaarOnEnter = (rawAadhaar) => {
    const number = String(rawAadhaar || "").replace(/\D/g, "").slice(0, 12);
    if (number.length !== 12) return;
    sendAadhaarOtp(number, { requireConsent: false, openOtpSheet: false });
  };

  const openAadhaarOtpSheet = () => {
    setAadhaarOtpDigits(["", "", "", "", "", ""]);
    openSheet("aadhaarOtp");
    setTimeout(() => aadhaarOtpRefs.current[0]?.focus(), 320);
  };

  const verifyAadhaarOtp = async () => {
    const otpValue = aadhaarOtpDigits.join("");
    if (otpValue.length !== 6) {
      setAadhaarOtpError("Enter the 6-digit OTP");
      return;
    }

    setAadhaarOtpVerifying(true);
    setAadhaarOtpError("");

    try {
      const res = await axios.post(
        VERIFY_AADHAAR_OTP_URL,
        {
          aadhaar_number: getAadhaarNumberSecure() || aadhaarNumber,
          otp: otpValue,
          referenceId: getAadhaarReferenceSecure(),
        },
        { timeout: 45000 }
      );

      const parsed = parseAadhaarVerifyResponse(res.data);
      const isSuccess =
        parsed.responseCode === "SRC001" &&
        parsed.profile &&
        Object.keys(parsed.profile).length > 0;

      const responseMessage = String(parsed.responseMessage || "").toLowerCase();
      const isFailure =
        parsed.responseCode === "EAE168" ||
        responseMessage.includes("invalid") ||
        responseMessage.includes("incorrect") ||
        responseMessage.includes("failed");

      if (isSuccess) {
        const mapped = mapAadhaarProfile(
          parsed.profile,
          getAadhaarNumberSecure() || aadhaarNumber
        );
        setAadhaarProfile(mapped);
        setAadhaarConsent(true);
        setAadhaarError("");
        mergeCustomerSession({
          saved_fields: {
            ...(getCustomerSession()?.saved_fields || {}),
            aadhaar_verified: "1",
            aadhaar_number: mapped.aadhaar,
            aadhaar_profile: mapped,
            aadhaar_dob: mapped.dobRaw || mapped.dob,
            aadhaar_gender: mapped.gender,
          },
        });
        closeSheet();
        return;
      }

      const errorMsg =
        res.data?.data?.responseMessage ||
        res.data?.message ||
        res.data?.error ||
        parsed.responseMessage ||
        (isFailure
          ? "Incorrect OTP. Please try again."
          : "OTP verification failed. Please try again.");
      setAadhaarOtpError(errorMsg);
    } catch (err) {
      setAadhaarOtpError(
        "OTP verification failed. Please check your internet connection and try again."
      );
    } finally {
      setAadhaarOtpVerifying(false);
    }
  };

  const handleAadhaarOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setAadhaarOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setAadhaarOtpError("");
    if (digit && index < 5) aadhaarOtpRefs.current[index + 1]?.focus();
  };

  const handleAadhaarOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !aadhaarOtpDigits[index] && index > 0) {
      aadhaarOtpRefs.current[index - 1]?.focus();
    }
  };

  const submitAadhaar = () => {
    if (aadhaarProfile) {
      setAadhaarError("");
      setActiveReferenceTab(0);
      setReferenceError("");
      setScreen("referenceDetails");
      return;
    }

    if (!aadhaarConsent) {
      setAadhaarError("Please accept Aadhaar verification consent");
      return;
    }

    const storedNumber = getAadhaarNumberSecure() || aadhaarNumber;
    const storedRef = getAadhaarReferenceSecure();
    if (
      storedNumber.replace(/\D/g, "").length === 12 &&
      storedRef &&
      aadhaarStatus &&
      !aadhaarError
    ) {
      openAadhaarOtpSheet();
      return;
    }

    sendAadhaarOtp(undefined, { requireConsent: true, openOtpSheet: true });
  };

  const updateDisburseBank = (field, value) => {
    setDisburseBank((prev) => ({ ...prev, [field]: value }));
    setDisburseBankError("");
    setDisburseBankSuccess("");
  };

  const proceedAfterBankVerification = () => {
    if (addBankSource === "aaAccount") {
      const matchedBank =
        ALL_BANKS.find((bank) => bank.name === disburseBank.bankName) ||
        ALL_BANKS[0];
      const last4 = disburseBank.accountNumber.slice(-4);
      const newAccount = {
        id: `acc-${Date.now()}`,
        bankId: matchedBank.id,
        type: "Savings account",
        masked: `*********${last4}`,
      };
      setBankAccounts((prev) => [...prev, newAccount]);
      setSelectedAccountId(newAccount.id);
      setSelectedBankId(matchedBank.id);
      setDisburseBank({
        bankName: "",
        holderName: "",
        accountNumber: "",
        ifsc: "",
      });
      setDisburseBankSuccess("");
      setScreen("aaAccount");
      return;
    }

    setDisburseBankSuccess("");
    setHoldMessage(
      "Your bank account has been verified. Disbursement will be processed shortly."
    );
    setScreen("hold");
  };

  const submitDisburseBank = async () => {
    if (!disburseBank.bankName) {
      setDisburseBankError("Select bank name");
      return;
    }
    if (!disburseBank.holderName.trim()) {
      setDisburseBankError("Enter account holder name");
      return;
    }
    if (!/^\d{9,18}$/.test(disburseBank.accountNumber)) {
      setDisburseBankError("Enter a valid account number");
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(disburseBank.ifsc)) {
      setDisburseBankError("Enter a valid IFSC code (e.g. HDFC0001234)");
      return;
    }

    setDisburseBankError("");
    setDisburseBankSuccess("");
    setDisburseBankVerifying(true);

    try {
      const payload = buildBankVerificationPayload({
        bankName: disburseBank.bankName,
        ifsc: disburseBank.ifsc,
        holderName: disburseBank.holderName,
        accountNumber: disburseBank.accountNumber,
      });

      const resp = await axios.post(BANK_AC_VERIFY_URL, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 45000,
        validateStatus: () => true,
      });

      if (!isBankVerificationSuccess(resp.data)) {
        setDisburseBankError(getBankVerificationMessage(resp.data));
        return;
      }

      const successMessage = getBankVerificationMessage(
        resp.data,
        "Account verified successfully."
      );
      const bankingId = getBankVerificationBankingId(resp.data);
      if (bankingId) setCustomerBankingId(bankingId);

      mergeCustomerSession({
        saved_fields: {
          ...(getCustomerSession()?.saved_fields || {}),
          disburse_bank: disburseBank,
          ...(bankingId ? { cust_banking_id: bankingId } : {}),
        },
      });

      setDisburseBankSuccess(successMessage);
      setDisburseBankVerifying(false);

      setTimeout(() => {
        proceedAfterBankVerification();
      }, 1800);
    } catch {
      setDisburseBankError("Bank verification failed. Please try again.");
    } finally {
      setDisburseBankVerifying(false);
    }
  };

  const openAddBankFromAa = () => {
    setAddBankSource("aaAccount");
    setDisburseBankError("");
    setDisburseBankSuccess("");
    setDisburseBank({
      bankName: getBank(selectedBankId).name,
      holderName: panProfile?.name || "",
      accountNumber: "",
      ifsc: "",
    });
    setScreen("addBankDetails");
  };

  const openSheet = (name) => setSheet(name);
  const closeSheet = () => {
    setSheetVisible(false);
    setTimeout(() => setSheet(null), 280);
  };

  const sendOtp = async () => {
    if (!MOBILE_REGEX.test(mobile)) {
      setMobileError("Enter a valid 10-digit mobile number");
      return;
    }
    if (!consent) {
      setMobileError("Please allow credit bureau access to continue");
      return;
    }

    setOtpSending(true);
    setMobileError("");

    try {
      const params = new URLSearchParams();
      params.append("mobile", mobile);

      const resp = await axios.post(SEND_OTP_URL, params, {
        headers: fundswallaOtpHeaders(),
        timeout: 20000,
      });

      const apiResponse = resp.data?.api_response || {};
      const apiReturn = apiResponse?.return;
      const apiMessage = Array.isArray(apiResponse?.message)
        ? apiResponse.message[0]
        : apiResponse?.message || "";

      if (
        apiReturn === false ||
        apiResponse?.status_code === 411 ||
        String(apiMessage).toLowerCase().includes("invalid")
      ) {
        setMobileError(apiMessage || "Invalid mobile number");
        return;
      }

      if (apiReturn === true || resp.data?.status === true) {
        setOtpDigits(["", "", "", "", "", ""]);
        setOtpError("");
        setResendTimer(OTP_RESEND_SECONDS);
        openSheet("otp");
        setTimeout(() => otpRefs.current[0]?.focus(), 320);
        return;
      }

      setMobileError(resp.data?.message || apiMessage || "Failed to send OTP");
    } catch (err) {
      setMobileError("Could not send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setOtpError("");
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    pasted.split("").forEach((digit, i) => {
      next[i] = digit;
    });
    setOtpDigits(next);
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const verifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length !== 6) {
      setOtpError("Enter the 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");

    try {
      const params = new URLSearchParams();
      params.append("mobile", mobile);
      params.append("otp", otp);

      const resp = await axios.post(VERIFY_OTP_URL, params, {
        headers: fundswallaOtpHeaders(),
        timeout: 20000,
      });

      if (resp.data?.status === true) {
        try {
          const loginResult = await loginCustomerByMobile(mobile);
          const stored = getCustomerSession();
          const targetScreen = loginResult.ok
            ? pickResumeScreen(stored, loginResult.login)
            : "location";

          if (loginResult.ok && loginResult.login) {
            restoreFromLoginSession({
              ...loginResult.login,
              screen: targetScreen,
            });
          }

          closeSheet();
          setTimeout(() => setScreen(targetScreen), 280);
        } catch {
          closeSheet();
          setTimeout(() => setScreen("location"), 280);
        }
        return;
      }

      setOtpError(resp.data?.message || "Entered OTP is incorrect");
    } catch (err) {
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const requestLocation = () => {
    setLocationError("");
    setLocationLoading(true);

    if (!navigator.geolocation) {
      setLocationLoading(false);
      setLocationError("Location is not supported on this browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = String(position.coords.latitude || "");
        const longitude = String(position.coords.longitude || "");
        const next = {
          latitude,
          longitude,
          coordinates: latitude && longitude ? `${latitude},${longitude}` : "",
        };
        setLocationCoords(next);
        persistLocation(next);
        setLocationLoading(false);
        await saveCustomerProgress(2, {
          latitude,
          longitude,
          coordinates: next.coordinates,
        });
        setScreen("details");
      },
      () => {
        setLocationLoading(false);
        setLocationError("Please allow location to continue");
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  };

  const applyVerifiedPanProfile = (panValue, verifyData) => {
    const panInfo = verifyData?.data || {};
    const fullName =
      panInfo.fullName ||
      [panInfo.firstName, panInfo.middleName, panInfo.lastName]
        .filter(Boolean)
        .join(" ") ||
      "—";

    const nextProfile = {
      name: fullName,
      fatherName:
        panInfo.fatherName ||
        panInfo.father_name ||
        panInfo.fathersName ||
        "—",
      pan: panInfo.pan || panValue,
      dob: formatDob(panInfo.dob || panInfo.dateOfBirth),
      gender: panInfo.gender || "",
      address: panInfo.address || "",
      aadhaarLinked: panInfo.aadhaarLinked,
      maskedAadhaarNumber: panInfo.maskedAadhaarNumber || "",
      responseCode: verifyData.responseCode || "",
      responseMessage: String(verifyData.responseMessage || ""),
      referenceId: verifyData.referenceId || "",
      firstName: panInfo.firstName || "",
      lastName: panInfo.lastName || "",
      category: panInfo.category || "",
    };
    setPanProfile(nextProfile);
    mergeCustomerSession({
      saved_fields: {
        ...(getCustomerSession()?.saved_fields || {}),
        pan: panValue,
        pan_profile: nextProfile,
      },
    });
  };

  const runPanApis = async (rawPan) => {
    const panValue = String(rawPan || "").trim().toUpperCase();
    if (panValue.length !== 10 || !PAN_REGEX.test(panValue)) {
      return { ok: false, reason: "invalid" };
    }

    const customerCheck = await checkCustomerByPan(panValue);
    if (customerCheck.blocked) {
      setPanError(
        customerCheck.message ||
          "This PAN is already registered. Cannot re-apply."
      );
      setPanStatus("");
      setPanProfile(null);
      return { ok: false, reason: "blocked" };
    }

    const verifyRes = await axios.get(VERIFY_PAN_URL, {
      params: { pan: panValue },
      timeout: 30000,
    });
    const verifyData = verifyRes.data?.data || {};
    const responseCode = verifyData.responseCode || "";
    const responseMessage = String(verifyData.responseMessage || "");

    if (
      responseCode === "ENI004" ||
      responseMessage.toLowerCase().includes("no information found")
    ) {
      setPanError("PAN does not exist. Enter your correct PAN.");
      setPanStatus("");
      setPanProfile(null);
      return { ok: false, reason: "not_found" };
    }

    if (responseCode !== "SRC001") {
      setPanError(responseMessage || "Unable to verify PAN. Please try again.");
      setPanStatus("");
      setPanProfile(null);
      return { ok: false, reason: "verify_fail" };
    }

    applyVerifiedPanProfile(panValue, verifyData);
    mergeCustomerSession({
      saved_fields: {
        ...(getCustomerSession()?.saved_fields || {}),
        pan: panValue,
        email: normalizeEmailInput(email),
      },
    });
    setPanError("");
    setPanStatus(
      customerCheck.message ||
        responseMessage ||
        "PAN Verification Complete!"
    );
    lastFetchedPanRef.current = panValue;
    return { ok: true };
  };

  const checkPanOnEnter = async (rawPan) => {
    const panValue = String(rawPan || "").trim().toUpperCase();
    if (panValue.length !== 10 || !PAN_REGEX.test(panValue)) return;
    if (panCheckLockRef.current) return;
    if (lastFetchedPanRef.current === panValue && panProfile?.pan === panValue) return;

    panCheckLockRef.current = true;
    setPanChecking(true);
    setPanError("");
    setPanStatus("");

    try {
      await runPanApis(panValue);
    } catch {
      setPanError("Unable to check PAN status. Please try again.");
      setPanStatus("");
      setPanProfile(null);
    } finally {
      panCheckLockRef.current = false;
      setPanChecking(false);
    }
  };

  const checkEligibility = async () => {
    const panValue = pan.trim().toUpperCase();
    const emailValue = normalizeEmailInput(email);
    const panMsg = !panValue
      ? "PAN is required"
      : PAN_REGEX.test(panValue)
        ? ""
        : "Enter a valid PAN (e.g. ABCDE1234F)";
    const emailMsg = validateEmail(emailValue);

    if (panMsg) {
      setPanError(panMsg);
      setPanStatus("");
    }
    setEmailError(emailMsg);
    if (panMsg || emailMsg) return;

    if (panError && /cannot re-apply/i.test(panError)) return;

    if (lastFetchedPanRef.current === panValue && panProfile?.pan === panValue && !panError) {
      openSheet("confirm");
      return;
    }

    setChecking(true);

    try {
      const result = await runPanApis(panValue);
      if (result.ok) openSheet("confirm");
    } catch {
      setPanError("PAN verification failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const confirmAndProceed = async () => {
    closeSheet();
    await saveCustomerProgress(3);
    setTimeout(() => setScreen("employment"), 280);
  };

  const updateEmployment = (key, value) => {
    setEmployment((prev) => ({ ...prev, [key]: value }));
    setEmploymentError("");
  };

  const submitEmployment = async () => {
    if (!employment.employer.trim()) {
      setEmploymentError("Enter name of employer");
      return;
    }
    if (!employment.salary || Number(employment.salary) <= 0) {
      setEmploymentError("Enter a valid monthly salary");
      return;
    }
    if (!isValidSalaryDate(employment.salaryDate)) {
      setEmploymentError("Select a valid salary date (DDMMYY)");
      return;
    }
    if (!employment.loanAmount || Number(employment.loanAmount) <= 0) {
      setEmploymentError("Enter a valid loan amount");
      return;
    }
    if (!employment.loanPurpose) {
      setEmploymentError("Select loan purpose");
      return;
    }
    if (!employment.employmentType) {
      setEmploymentError("Select employment type");
      return;
    }
    if (!employment.salaryMode) {
      setEmploymentError("Select mode of income");
      return;
    }

    setEmploymentError("");
    await saveCustomerProgress(3);
    setScreen("processBank");
  };

  const otpValue = otpDigits.join("");
  const aaOtpValue = aaOtpDigits.join("");

  const getLeadPayload = (overrides = {}) => {
    const stored = getCustomerSession() || {};
    const fields = stored.saved_fields || {};
    const storedLoc = readStoredLocation();
    const storedPanProfile = parseStoredJson(fields.pan_profile) || panProfile;
    const storedAadhaarSaved = parseStoredJson(fields.aadhaar_profile);
    const storedAadhaarProfile = {
      ...(storedAadhaarSaved || {}),
      ...(aadhaarProfile || {}),
      raw: {
        ...(storedAadhaarSaved?.raw || {}),
        ...(aadhaarProfile?.raw || {}),
      },
      dob:
        aadhaarProfile?.dob ||
        storedAadhaarSaved?.dob ||
        fields.aadhaar_dob ||
        "",
      dobRaw:
        aadhaarProfile?.dobRaw ||
        storedAadhaarSaved?.dobRaw ||
        fields.aadhaar_dob ||
        "",
      gender:
        aadhaarProfile?.gender ||
        storedAadhaarSaved?.gender ||
        fields.aadhaar_gender ||
        "",
    };
    const storedBank = parseStoredJson(fields.disburse_bank) || disburseBank;
    const storedRefs = parseStoredJson(fields.references) || references;

    return buildLeadApplicationPayload({
      mobile: mobile || stored.customer_mobile,
      email: email || fields.email || fields.email_personal,
      pan: pan || fields.pan,
      panProfile: storedPanProfile,
      aadhaarNumber:
        getAadhaarNumberSecure() || aadhaarNumber || fields.aadhaar_number,
      aadhaarProfile: storedAadhaarProfile,
      aadhaarReference: getAadhaarReferenceSecure(),
      locationCoords: {
        latitude:
          locationCoords.latitude || fields.latitude || storedLoc.latitude,
        longitude:
          locationCoords.longitude || fields.longitude || storedLoc.longitude,
        coordinates:
          locationCoords.coordinates ||
          fields.coordinates ||
          storedLoc.coordinates,
      },
      employment: {
        employer: employment.employer || fields.employer || fields.employer_name,
        salary: employment.salary || fields.monthly_income || fields.salary,
        salaryDate: employment.salaryDate || fields.salary_date,
        loanAmount: employment.loanAmount || fields.loan_amount,
        loanPurpose: employment.loanPurpose || fields.loan_purpose,
        employmentType: employment.employmentType || fields.employment_type,
        salaryMode: employment.salaryMode || fields.salary_mode,
      },
      references: Array.isArray(storedRefs) && storedRefs.length ? storedRefs : references,
      disburseBank: {
        bankName: disburseBank.bankName || storedBank?.bankName || "",
        holderName: disburseBank.holderName || storedBank?.holderName || "",
        accountNumber: disburseBank.accountNumber || storedBank?.accountNumber || "",
        ifsc: disburseBank.ifsc || storedBank?.ifsc || "",
      },
      customerBankingId: customerBankingId || fields.cust_banking_id,
      customerLeadId: customerLeadId || stored.lead_id,
      customerStep,
      selfiePhoto: overrides.selfiePhoto || selfiePhoto,
      consent: true,
      aadhaarConsent: aadhaarConsent || Boolean(storedAadhaarProfile),
      aaMobile,
      selectedBankId,
    });
  };

  const submitLeadAndContinue = async (photoOverride) => {
    if (leadSubmitting) return;

    if (leadSubmittedRef.current && customerLeadId) {
      setScreen("selfieSuccess");
      return;
    }

    const photo =
      (typeof photoOverride === "string" && photoOverride) || selfiePhoto;
    if (photo) {
      setSelfiePhoto(photo);
      persistSelfie(photo);
    }

    setLeadSubmitError("");
    setLeadSubmitting(true);
    setScreen("leadSubmitting");

    try {
      const result = await submitLeadApplication(
        LEAD_APPLICATION_URL,
        getLeadPayload({ selfiePhoto: photo })
      );

      if (!result.ok) {
        setLeadSubmitError(
          result.message || "Application could not be submitted. Please try again."
        );
        setScreen("leadSubmitFail");
        return;
      }

      if (result.leadId) setCustomerLeadId(result.leadId);
      leadSubmittedRef.current = true;
      mergeCustomerSession({
        customer_mobile: mobile,
        last_screen: "selfieSuccess",
        lead_id: result.leadId || customerLeadId,
        saved_fields: {
          ...(getCustomerSession()?.saved_fields || {}),
          lead_submitted: "1",
          last_screen: "selfieSuccess",
        },
      });
      setScreen("selfieSuccess");
    } catch (err) {
      const errors = err?.response?.data?.errors;
      const apiMessage =
        err?.response?.data?.message ||
        (errors && typeof errors === "object"
          ? Object.values(errors).filter(Boolean).join(" ")
          : "");
      setLeadSubmitError(
        apiMessage ||
          (err?.message === "Network Error"
            ? "Could not reach the server. Please try again."
            : "Application could not be submitted. Please try again.")
      );
      setScreen("leadSubmitFail");
    } finally {
      setLeadSubmitting(false);
    }
  };

  leadPayloadRef.current = getLeadPayload();

  if (sessionBooting) {
    return (
      <div className="fw-apply">
        <div className="fw-apply__phone">
          <section className="fw-screen fw-screen--plain fw-fade">
            <p className="fw-sub fw-resume-loading">Restoring your application...</p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="fw-apply">
      <div className="fw-apply__phone">
        {screen === "landing" && (
          <section className="fw-screen fw-screen--landing fw-fade">
            <div className="fw-hero">
              <div className="fw-brand">
                <span className="fw-brand__top">FUNDS</span>
                <span className="fw-brand__bottom">WALLE</span>
              </div>
              <h1>Instant Loan for Salaried Employees</h1>
              <p className="fw-hero__amount">
                upto <b>₹1,00,000</b>
              </p>
            </div>

            <div className="fw-benefits">
              <article className="fw-card">
                <span className="fw-card__icon fw-card__icon--gold"><FiZap /></span>
                <div>
                  <h2>Instant Loan</h2>
                  <p>Fast approval and disbursal within 24 hours.</p>
                </div>
              </article>
              <article className="fw-card">
                <span className="fw-card__icon fw-card__icon--purple"><FiFileText /></span>
                <div>
                  <h2>100% Paperless</h2>
                  <p>Apply easily through a secure digital process.</p>
                </div>
              </article>
              <article className="fw-card">
                <span className="fw-card__icon fw-card__icon--blue"><FiCalendar /></span>
                <div>
                  <h2>Flexible EMIs</h2>
                  <p>Choose a repayment plan that fits your budget.</p>
                </div>
              </article>
            </div>

            <button
              type="button"
              className="fw-cta"
              onClick={() => openSheet("mobile")}
            >
              Apply Now
              <span className="fw-cta__arrow" aria-hidden="true"><FiArrowRight /></span>
            </button>
          </section>
        )}

        {screen === "location" && (
          <section className="fw-screen fw-screen--plain fw-fade">
            <h1 className="fw-title">We need the following permission to proceed</h1>
            <div className="fw-permission">
              <span className="fw-permission__icon" aria-hidden="true"><FiMapPin /></span>
              <div>
                <h2>Location Permission</h2>
                <p>to show you offers & alerts exclusive to your location</p>
              </div>
            </div>
            {locationError && <p className="fw-error">{locationError}</p>}
            <div className="fw-screen__footer">
              <button
                type="button"
                className="fw-btn fw-btn--dark fw-btn--full"
                onClick={requestLocation}
                disabled={locationLoading}
              >
                {locationLoading ? "Requesting..." : "Proceed"}
              </button>
            </div>
          </section>
        )}

        {screen === "details" && (
          <section className="fw-screen fw-screen--plain fw-fade">
            <p className="fw-progress-label">Processing 10%</p>
            <div className="fw-progress"><span style={{ width: "10%" }} /></div>
            <h1 className="fw-title">Basic Details</h1>
            <p className="fw-sub">Enter the below details to check your eligibility quickly.</p>

            <label className="fw-field">
              <span>Enter your PAN Number</span>
              <input
                value={pan}
                maxLength={10}
                placeholder="ASDFR3128J"
                autoCapitalize="characters"
                onChange={(e) => {
                  const next = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 10);
                  setPan(next);
                  if (next.length < 10) {
                    setPanError("");
                    setPanStatus("");
                    setPanProfile(null);
                    lastFetchedPanRef.current = "";
                    return;
                  }
                  checkPanOnEnter(next);
                }}
              />
              {panChecking && <em className="fw-field__info">Checking PAN status...</em>}
              {!panChecking && panError && <em>{panError}</em>}
              {!panChecking && !panError && panStatus && (
                <em className="fw-field__ok">{panStatus}</em>
              )}
            </label>

            <label className="fw-field">
              <span>Enter your Email ID</span>
              <input
                type="email"
                value={email}
                placeholder="joey657@gmail.com"
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
              />
              {emailError && <em>{emailError}</em>}
            </label>

            <div className="fw-actions">
              <button type="button" className="fw-btn fw-btn--ghost" onClick={() => setScreen("location")}>
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={checkEligibility}
                disabled={checking}
              >
                {checking ? "Checking..." : "Check Eligibility"}
              </button>
            </div>
          </section>
        )}

        {screen === "employment" && (
          <section className="fw-screen fw-screen--form fw-fade">
            <h1 className="fw-title">Employment Details</h1>

            <label className="fw-field">
              <span>Name of Employer</span>
              <input
                value={employment.employer}
                placeholder="Reliance Industries Ltd."
                onChange={(e) => updateEmployment("employer", e.target.value)}
              />
            </label>

            <label className="fw-field">
              <span>Net Monthly Salary (₹)</span>
              <input
                inputMode="numeric"
                value={employment.salary}
                placeholder="eg. 30,000"
                onChange={(e) =>
                  updateEmployment("salary", e.target.value.replace(/\D/g, "").slice(0, 8))
                }
              />
            </label>

            <FwDayCalendar
              id="salaryDate"
              label="Salary Date"
              value={employment.salaryDate}
              placeholder="Select date (DDMMYY)"
              openKey={openDropdown}
              setOpenKey={setOpenDropdown}
              onChange={(val) => updateEmployment("salaryDate", val)}
            />

            <label className="fw-field">
              <span>Loan Amount Required (₹)</span>
              <input
                inputMode="numeric"
                value={employment.loanAmount}
                placeholder="eg. 50,000"
                onChange={(e) =>
                  updateEmployment("loanAmount", e.target.value.replace(/\D/g, "").slice(0, 7))
                }
              />
            </label>

            <FwDropdown
              id="loanPurpose"
              label="Loan Purpose"
              value={employment.loanPurpose}
              placeholder="Select loan purpose"
              options={LOAN_PURPOSES}
              openKey={openDropdown}
              setOpenKey={setOpenDropdown}
              onChange={(val) => updateEmployment("loanPurpose", val)}
            />

            <FwDropdown
              id="employmentType"
              label="Employment Type"
              value={employment.employmentType}
              placeholder="Select employment type"
              options={EMPLOYMENT_TYPES}
              openKey={openDropdown}
              setOpenKey={setOpenDropdown}
              onChange={(val) => updateEmployment("employmentType", val)}
            />

            <FwDropdown
              id="salaryMode"
              label="Salary Received via"
              value={employment.salaryMode}
              placeholder="Select mode of income"
              options={SALARY_MODES}
              openKey={openDropdown}
              setOpenKey={setOpenDropdown}
              onChange={(val) => updateEmployment("salaryMode", val)}
            />

            {employmentError && <p className="fw-error">{employmentError}</p>}

            <div className="fw-actions">
              <button type="button" className="fw-btn fw-btn--ghost" onClick={() => setScreen("details")}>
                Back
              </button>
              <button type="button" className="fw-btn fw-btn--primary" onClick={submitEmployment}>
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "processing" && (
          <section className="fw-screen fw-screen--status fw-screen--processing fw-fade">
            <div className="fw-status__copy">
              <h1 className="fw-title">Preparing Your Personalized Loan Offer</h1>
              <p className="fw-sub">
                We’re currently reviewing your application to craft the most suitable loan
                offer tailored to your financial needs.
              </p>
            </div>

            <div className="fw-status__art" aria-hidden="true">
              <span className="fw-status__pulse" />
              <span className="fw-status__pulse fw-status__pulse--2" />
              <div className="fw-status__doc">
                <FiUser />
                <i /><i /><i />
                <span className="fw-status__scan" />
              </div>
              <span className="fw-status__lens"><FiSearch /></span>
            </div>

            <p className="fw-status__note">
              Please hold while we complete the necessary verification checks.
            </p>
            <p className="fw-status__step">{VERIFY_STEPS[verifyStep]}</p>

            <div className="fw-status__progress" aria-hidden="true">
              <span />
            </div>

            <div className="fw-status__dots" aria-hidden="true">
              <i /><i /><i />
            </div>
          </section>
        )}

        {screen === "approved" && (
          <section className="fw-screen fw-screen--status fw-screen--approved fw-fade">
            <div className="fw-approved">
              <div className="fw-approved__fx" aria-hidden="true">
                <span className="fw-approved__glow" />
                <span className="fw-approved__ring" />
                <span className="fw-approved__ring fw-approved__ring--2" />
                {Array.from({ length: 14 }, (_, i) => (
                  <span key={i} className={`fw-approved__spark fw-approved__spark--${i + 1}`} />
                ))}
              </div>

              <div className="fw-approved__hero">
                <span className="fw-approved__icon"><FiCheck /></span>
              </div>

              <h2 className="fw-approved__title">Congratulations!</h2>
              <p className="fw-approved__msg">Your loan has been successfully approved.</p>
              {employment.loanAmount && (
                <p className="fw-approved__amount">
                  ₹{Number(employment.loanAmount).toLocaleString("en-IN")}
                </p>
              )}
              <span className="fw-approved__chip">Pre-approved offer ready</span>
            </div>

            <div className="fw-screen__footer fw-approved__footer">
              <button
                type="button"
                className="fw-btn fw-btn--dark fw-btn--full"
                onClick={() => setScreen("offer")}
              >
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "offer" && (
          <section className="fw-screen fw-screen--offer fw-fade">
            <div className="fw-offer__bg" aria-hidden="true" />

            <div className="fw-offer__content">
              <header className="fw-offer__head">
                <span className="fw-offer__badge">Loan Approved</span>
                <h1 className="fw-offer__title">Congratulations!</h1>
                <p className="fw-offer__label">Your approved loan amount</p>
                <div className="fw-offer__amount-card">
                  <strong className="fw-offer__amount">
                    {formatLoanAmount(employment.loanAmount)}
                  </strong>
                </div>
              </header>

              <div className="fw-offer__hero">
                <OfferHeroArt />
              </div>

              <div className={`fw-offer__meta ${offerSeconds <= 120 ? "is-urgent" : ""}`}>
                <p>Exclusive Offer Just for You!</p>
                <span>Offer expires in</span>
                <div className="fw-offer__timer" aria-live="polite">
                  {formatCountdown(offerSeconds)
                    .split(":")
                    .map((part, index) => (
                      <React.Fragment key={`${index}-${part}`}>
                        {index > 0 && <em>:</em>}
                        <b>{part}</b>
                      </React.Fragment>
                    ))}
                </div>
              </div>
            </div>

            <div className="fw-offer__action">
              <button
                type="button"
                className={`fw-btn fw-btn--dark fw-btn--full fw-offer__cta ${
                  offerSeconds === 0 ? "is-ready" : ""
                }`}
                disabled={offerSeconds > 0}
                onClick={() => {
                  setAddBankSource("offer");
                  setDisburseBankError("");
                  setDisburseBankSuccess("");
                  setDisburseBank((prev) => ({
                    ...prev,
                    holderName: prev.holderName || panProfile?.name || "",
                    bankName: prev.bankName || getBank(selectedBankId).name,
                  }));
                  setScreen("addBankDetails");
                }}
              >
                {offerSeconds > 0 ? "Withdraw Funds" : "Withdraw Funds Now"}
              </button>
              {offerSeconds > 0 && (
                <p className="fw-offer__cta-note">Button unlocks when timer ends</p>
              )}
            </div>
          </section>
        )}

        {screen === "addBankDetails" && (
          <section className="fw-screen fw-screen--form fw-screen--add-bank fw-fade">
            <h1 className="fw-title">Add Your Bank Account Details</h1>
            <p className="fw-sub fw-add-bank__sub">
              Please add your salary bank account details to receive the loan amount.
            </p>

            <div className="fw-add-bank__form">
              <FwDropdown
                id="disburseBankName"
                label="Bank Name"
                value={disburseBank.bankName}
                placeholder="Select bank name"
                options={ALL_BANKS.map((bank) => bank.name)}
                openKey={openDropdown}
                setOpenKey={setOpenDropdown}
                onChange={(val) => updateDisburseBank("bankName", val)}
              />

              <label className="fw-field">
                <span>Account Holder’s Name</span>
                <input
                  value={disburseBank.holderName}
                  placeholder="Joey Tribbiani"
                  onChange={(e) => updateDisburseBank("holderName", e.target.value)}
                />
              </label>

              <label className="fw-field">
                <span>Account Number</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={18}
                  value={disburseBank.accountNumber}
                  placeholder="eg. 587658763256785"
                  onChange={(e) =>
                    updateDisburseBank(
                      "accountNumber",
                      e.target.value.replace(/\D/g, "").slice(0, 18)
                    )
                  }
                />
              </label>

              <label className="fw-field">
                <span>IFSC Code</span>
                <input
                  value={disburseBank.ifsc}
                  maxLength={11}
                  placeholder="eg. HDFC0001234"
                  autoCapitalize="characters"
                  onChange={(e) =>
                    updateDisburseBank(
                      "ifsc",
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11)
                    )
                  }
                />
              </label>

              {disburseBankError && <p className="fw-error">{disburseBankError}</p>}
              {disburseBankSuccess && (
                <p className="fw-success">{disburseBankSuccess}</p>
              )}
            </div>

            <div className="fw-add-bank__safe">
              <strong>100% Safe and Secure</strong>
            </div>

            <div className="fw-actions">
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() =>
                  setScreen(addBankSource === "aaAccount" ? "aaAccount" : "offer")
                }
              >
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={submitDisburseBank}
                disabled={disburseBankVerifying || Boolean(disburseBankSuccess)}
              >
                {disburseBankVerifying
                  ? "Verifying..."
                  : disburseBankSuccess
                    ? "Verified"
                    : "Proceed"}
              </button>
            </div>
          </section>
        )}

        {screen === "processBank" && (
          <section className="fw-screen fw-screen--bank-intro fw-fade">
            <div className="fw-bank-intro__art" aria-hidden="true">
              <span className="fw-bank-intro__ring fw-bank-intro__ring--1" />
              <span className="fw-bank-intro__ring fw-bank-intro__ring--2" />
              <span className="fw-bank-intro__dot fw-bank-intro__dot--1" />
              <span className="fw-bank-intro__dot fw-bank-intro__dot--2" />
              <span className="fw-bank-intro__dot fw-bank-intro__dot--3" />
              <svg viewBox="0 0 220 180" fill="none" className="fw-bank-intro__svg">
                <circle cx="110" cy="90" r="70" fill="#eef3ff" className="fw-bank-intro__bg" />
                <g className="fw-bank-intro__doc">
                  <rect x="62" y="58" width="96" height="68" rx="8" fill="#fff" stroke="#d5d1e4" />
                  <path
                    className="fw-bank-intro__line fw-bank-intro__line--1"
                    d="M74 78h72"
                    stroke="#d5d1e4"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <path
                    className="fw-bank-intro__line fw-bank-intro__line--2"
                    d="M74 92h52"
                    stroke="#d5d1e4"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <path
                    className="fw-bank-intro__line fw-bank-intro__line--3"
                    d="M74 106h40"
                    stroke="#d5d1e4"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <rect className="fw-bank-intro__scan" x="68" y="64" width="84" height="6" rx="3" fill="#6b68a6" opacity="0.25" />
                </g>
                <g className="fw-bank-intro__plus">
                  <circle cx="146" cy="118" r="24" fill="#fff" stroke="#6b68a6" strokeWidth="3" />
                  <path d="M136 118h20M146 108v20" stroke="#6b68a6" strokeWidth="3" strokeLinecap="round" />
                </g>
              </svg>
            </div>
            <h1 className="fw-title fw-bank-intro__title">Process to Bank</h1>
            <p className="fw-sub fw-bank-intro__text">
              We&apos;ve received your employment details and are securely sharing them with the
              bank. Once we review your bank statement and complete our analysis, we&apos;ll share a
              suitable offer with you.
            </p>
            <div className="fw-screen__footer fw-bank-intro__footer">
              <button type="button" className="fw-btn fw-btn--dark fw-btn--full" onClick={() => setScreen("selectBank")}>
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "selectBank" && (
          <section className="fw-screen fw-screen--bank fw-fade">
            <div className="fw-bank-top">
              <button type="button" className="fw-bank-back" onClick={() => setScreen("processBank")} aria-label="Back">
                <FiArrowLeft />
              </button>
              <h1>Select your Bank</h1>
            </div>
            <label className="fw-bank-search">
              <FiSearch />
              <input
                value={bankSearch}
                placeholder="Search bank name"
                onChange={(e) => setBankSearch(e.target.value)}
              />
            </label>
            <div className="fw-bank-scroll">
              <h2>Popular Banks</h2>
              <div className="fw-bank-grid">
                {POPULAR_BANKS.filter((bank) =>
                  bank.name.toLowerCase().includes(bankSearch.trim().toLowerCase())
                ).map((bank) => (
                  <button
                    key={bank.id}
                    type="button"
                    className="fw-bank-card"
                    onClick={() => {
                      setSelectedBankId(bank.id);
                      setScreen("connectBank");
                    }}
                  >
                    <span className="fw-bank-logo" style={{ background: bank.color }}>
                      {bank.name.charAt(0)}
                    </span>
                    <small>{bank.name}</small>
                  </button>
                ))}
              </div>
              <h2>All Banks</h2>
              <div className="fw-bank-list">
                {filteredBanks.map((bank) => (
                  <button
                    key={`all-${bank.id}`}
                    type="button"
                    className="fw-bank-row"
                    onClick={() => {
                      setSelectedBankId(bank.id);
                      setScreen("connectBank");
                    }}
                  >
                    <span className="fw-bank-logo fw-bank-logo--sm" style={{ background: bank.color }}>
                      {bank.name.charAt(0)}
                    </span>
                    <span>{bank.name}</span>
                    <FiChevronRight />
                  </button>
                ))}
              </div>
            </div>
            <p className="fw-powered">Powered by <b>FUNDSWALLE</b></p>
          </section>
        )}

        {screen === "connectBank" && (
          <section className="fw-screen fw-screen--bank fw-fade">
            <div className="fw-bank-top">
              <button type="button" className="fw-bank-back" onClick={() => setScreen("selectBank")} aria-label="Back">
                <FiArrowLeft />
              </button>
              <h1>Connect your Bank</h1>
            </div>
            <div className="fw-bank-chip fw-bank-chip--selected">
              <span className="fw-bank-logo fw-bank-logo--sm" style={{ background: getBank(selectedBankId).color }}>
                {getBank(selectedBankId).name.charAt(0)}
              </span>
              <strong>{getBank(selectedBankId).name}</strong>
            </div>
            <div className="fw-connect-list">
              <button type="button" className="fw-connect-card fw-connect-card--featured" onClick={() => setScreen("aaMobile")}>
                <span className="fw-connect-card__badge">Fast, Secure &amp; Recommended</span>
                <span className="fw-connect-card__icon" aria-hidden="true">
                  <FiSmartphone />
                </span>
                <div className="fw-connect-card__body">
                  <strong>Fetch Using Phone Number</strong>
                  <p>Securely retrieve your bank details using your registered mobile number through an RBI regulated Account Aggregator.</p>
                </div>
                <FiChevronRight className="fw-connect-card__arrow" aria-hidden="true" />
              </button>
              <button type="button" className="fw-connect-card" onClick={() => setScreen("aaMobile")}>
                <span className="fw-connect-card__icon" aria-hidden="true">
                  <FiHome />
                </span>
                <div className="fw-connect-card__body">
                  <strong>Net Banking</strong>
                  <p>Conveniently connect by logging in with your Internet Banking User ID and Password.</p>
                </div>
                <FiChevronRight className="fw-connect-card__arrow" aria-hidden="true" />
              </button>
              <button type="button" className="fw-connect-card" onClick={() => setScreen("aaMobile")}>
                <span className="fw-connect-card__icon" aria-hidden="true">
                  <FiUpload />
                </span>
                <div className="fw-connect-card__body">
                  <strong>Upload Statement</strong>
                  <p>Easily upload your official bank statement document for verification.</p>
                </div>
                <FiChevronRight className="fw-connect-card__arrow" aria-hidden="true" />
              </button>
            </div>
            <p className="fw-powered">Powered by <b>FUNDSWALLE</b></p>
          </section>
        )}

        {screen === "aaMobile" && (
          <section className="fw-screen fw-screen--bank fw-fade">
            <div className="fw-bank-top">
              <button type="button" className="fw-bank-back" onClick={() => setScreen("connectBank")} aria-label="Back">
                <FiArrowLeft />
              </button>
              <h1>Account Aggregator Verification</h1>
            </div>
            <div className="fw-bank-chip">
              <span className="fw-bank-logo fw-bank-logo--sm" style={{ background: getBank(selectedBankId).color }}>
                {getBank(selectedBankId).name.charAt(0)}
              </span>
              <strong>{getBank(selectedBankId).name}</strong>
            </div>
            <label className="fw-field">
              <span>Enter the mobile number linked to your account</span>
              <div className="fw-mobile-input">
                <span>🇮🇳</span>
                <strong>+91</strong>
                <i />
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter your mobile number"
                  value={aaMobile}
                  onChange={(e) => {
                    setAaMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setAaMobileError("");
                  }}
                />
              </div>
              {aaMobileError && <p className="fw-error">{aaMobileError}</p>}
            </label>
            <div className="fw-screen__footer">
              <button type="button" className="fw-btn fw-btn--dark fw-btn--full" onClick={openAaOtpSheet}>
                Proceed
              </button>
            </div>
            <p className="fw-powered">Powered by <b>FUNDSWALLE</b></p>
          </section>
        )}

        {screen === "aaAccount" && (
          <section className="fw-screen fw-screen--bank fw-fade">
            <div className="fw-bank-top">
              <button type="button" className="fw-bank-back" onClick={() => setScreen("aaMobile")} aria-label="Back">
                <FiArrowLeft />
              </button>
              <h1>Account Aggregator Verification</h1>
            </div>
            <p className="fw-bank-label">Choose the Bank Account you wish to use.</p>
            <div className="fw-bank-scroll">
              {bankAccounts.map((account) => {
                const bank = getBank(account.bankId);
                return (
                  <button
                    key={account.id}
                    type="button"
                    className={`fw-account-card ${selectedAccountId === account.id ? "is-selected" : ""}`}
                    onClick={() => setSelectedAccountId(account.id)}
                  >
                    <span className="fw-bank-logo fw-bank-logo--sm" style={{ background: bank.color }}>
                      {bank.name.charAt(0)}
                    </span>
                    <div>
                      <strong>{bank.name}</strong>
                      <small>{account.type} {account.masked}</small>
                    </div>
                    <span className="fw-account-card__radio" />
                  </button>
                );
              })}
            </div>
            <div className="fw-screen__footer fw-bank-footer-stack">
              <button type="button" className="fw-btn fw-btn--dark fw-btn--full" onClick={() => setScreen("aaConsent")}>
                Proceed
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--soft fw-btn--full"
                onClick={openAddBankFromAa}
              >
                Add Another Account
              </button>
            </div>
            <p className="fw-powered">Powered by <b>FUNDSWALLE</b></p>
          </section>
        )}

        {screen === "aaConsent" && (
          <section className="fw-screen fw-screen--bank fw-fade">
            <div className="fw-bank-top">
              <button type="button" className="fw-bank-back" onClick={() => setScreen("aaAccount")} aria-label="Back">
                <FiArrowLeft />
              </button>
              <h1>Account Aggregator Verification</h1>
            </div>
            <div className="fw-bank-scroll">
              <h2 className="fw-bank-section-title">Account Summary</h2>
              <div className="fw-summary-card">
                <span className="fw-bank-logo fw-bank-logo--sm" style={{ background: getBank(selectedBankId).color }}>
                  {getBank(selectedBankId).name.charAt(0)}
                </span>
                <div>
                  <strong>{getBank(selectedBankId).name}</strong>
                  <small>XXXXXXXXXX7797</small>
                  <em>Savings Account</em>
                </div>
              </div>
              <h2 className="fw-bank-section-title">Consent</h2>
              <div className="fw-consent-card">
                <div className="fw-consent-card__head">
                  <label><input type="checkbox" defaultChecked readOnly /> ONETIME</label>
                  <button type="button">More details</button>
                </div>
                <p><b>Consent Period</b> 07/May/2025 - 07/Aug/2025</p>
                <p><b>Data Life</b> 1 month</p>
                <p><b>Frequency</b> 1 time per month</p>
                <div className="fw-consent-tags">
                  <button type="button" onClick={() => setScreen("profile")}>
                    Profile
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentHistoryTab("loans");
                      setScreen("paymentHistory");
                    }}
                  >
                    Summary
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentHistoryTab("transactions");
                      setScreen("paymentHistory");
                    }}
                  >
                    Transactions
                  </button>
                </div>
                <p><b>Purpose</b> To assess your loan eligibility</p>
              </div>
            </div>
            <div className="fw-screen__footer fw-bank-footer-actions">
              <button type="button" className="fw-btn fw-btn--ghost" onClick={() => setScreen("aaAccount")}>
                Decline
              </button>
              <button type="button" className="fw-btn fw-btn--dark" onClick={() => setScreen("bankSuccess")}>
                Agree
              </button>
            </div>
            <p className="fw-powered">Powered by <b>FUNDSWALLE</b></p>
          </section>
        )}

        {screen === "bankSuccess" && (
          <section className="fw-screen fw-screen--bank-success fw-fade">
            <div className="fw-bank-success">
              <div className="fw-bank-success__icon-wrap" aria-hidden="true">
                <span className="fw-bank-success__ring" />
                <span className="fw-bank-success__ring fw-bank-success__ring--2" />
                <span className="fw-bank-success__icon"><FiCheck /></span>
              </div>
              <h2 className="fw-bank-success__title">Success!</h2>
              <p className="fw-bank-success__msg">
                Your bank account has been linked successfully.
              </p>
            </div>
            <div className="fw-screen__footer">
              <button
                type="button"
                className="fw-btn fw-btn--dark fw-btn--full"
                onClick={() => {
                  setAadhaarError("");
                  setAadhaarProfile(null);
                  setScreen("aadhaarKyc");
                }}
              >
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "aadhaarKyc" && (
          <section className="fw-screen fw-screen--form fw-screen--aadhaar fw-fade">
            <h1 className="fw-title">Aadhaar Verification</h1>
            <p className="fw-sub fw-aadhaar__sub">
              Enter your Aadhaar number to verify identity. Please match details with your Aadhaar card.
            </p>

            <label className="fw-field">
              <span>Enter your Aadhaar Number</span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={14}
                placeholder="XXXX XXXX XXXX"
                value={formatAadhaarDisplay(aadhaarNumber)}
                disabled={!!aadhaarProfile || aadhaarSending}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, "").slice(0, 12);
                  setAadhaarNumber(next);
                  setAadhaarError("");
                  setAadhaarStatus("");
                  setAadhaarProfile(null);
                  if (next.length !== 12) {
                    setAadhaarReferenceSecure("");
                  }
                  if (next.length === 12) {
                    checkAadhaarOnEnter(next);
                  }
                }}
              />
              {aadhaarSending && <em className="fw-field__info">Checking Aadhaar...</em>}
              {!aadhaarSending && aadhaarError && <em className="fw-error">{aadhaarError}</em>}
              {!aadhaarSending && !aadhaarError && aadhaarStatus && (
                <em className="fw-field__ok">{aadhaarStatus}</em>
              )}
            </label>

            {!aadhaarProfile && (
              <label className="fw-check fw-aadhaar-consent">
                <input
                  type="checkbox"
                  checked={aadhaarConsent}
                  onChange={() => {
                    setAadhaarConsent((prev) => !prev);
                    setAadhaarError("");
                  }}
                />
                <span>
                  I authorize <b>Aadhaar verification</b> and receipt of{" "}
                  <b>digital communications</b>.
                </span>
              </label>
            )}

            {aadhaarProfile && (
              <div className="fw-aadhaar-preview">
                <p className="fw-aadhaar-preview__ask">Is this information correct?</p>
                <div className="fw-aadhaar-idcard" role="img" aria-label="Aadhaar card preview">
                  <div className="fw-aadhaar-idcard__secure" aria-hidden="true" />
                  <div className="fw-aadhaar-idcard__rail" aria-hidden="true">
                    <span>आधार - आम आदमी का अधिकार</span>
                  </div>

                  <div className="fw-aadhaar-idcard__body">
                    <div className="fw-aadhaar-idcard__head">
                      <img className="fw-aadhaar-idcard__emblem" src={indiaEmblem} alt="" />
                      <div className="fw-aadhaar-idcard__govt">
                        <svg
                          className="fw-aadhaar-idcard__brush"
                          viewBox="0 0 220 36"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <path
                            fill="#FF9933"
                            d="M4 4c28-3 58-2 86 1 34 3 62 2 96-1 8-.7 18 1 26 3-10 2-22 2-34 3-40 4-78 3-116 0C42 8 22 8 8 10 2 8 0 6 4 4z"
                          />
                          <path
                            fill="#138808"
                            d="M2 22c30-2 64 1 96 2 38 2 72 0 104-3 6-.6 14 0 18 2-14 3-34 4-52 5-46 3-90 1-132-2C24 25 10 25 4 26c-2-1-2-3-2-4z"
                          />
                        </svg>
                        <b>भारत सरकार</b>
                        <em>GOVERNMENT OF INDIA</em>
                      </div>
                      <img className="fw-aadhaar-idcard__logo" src={aadhaarLogoRight} alt="" />
                    </div>

                    <div className="fw-aadhaar-idcard__row">
                      <div className="fw-aadhaar-idcard__photo" aria-hidden="true">
                        <svg viewBox="0 0 80 96" width="100%" height="100%">
                          <rect width="80" height="96" fill="#b9bbd0" />
                          <circle cx="40" cy="34" r="18" fill="#4a4a58" />
                          <ellipse cx="40" cy="88" rx="30" ry="28" fill="#4a4a58" />
                        </svg>
                      </div>

                      <div className="fw-aadhaar-idcard__meta">
                        <p>
                          <span>Name:</span> {aadhaarProfile.name}
                        </p>
                        <p>
                          <span>DOB:</span> {aadhaarProfile.dob}
                        </p>
                        <p>
                          <span>Gender:</span> {String(aadhaarProfile.gender || "").toUpperCase()}
                        </p>
                      </div>
                    </div>

                    <div className="fw-aadhaar-idcard__uid">
                      {`XXXX XXXX ${String(aadhaarProfile.aadhaar || "").slice(-4)}`}
                    </div>

                    <div className="fw-aadhaar-idcard__brand">
                      <img src={aadhaarLogoMark} alt="" />
                      <b>आधार</b>
                      <span>Aadhaar</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="fw-actions">
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() => {
                  if (aadhaarProfile) {
                    setAadhaarProfile(null);
                    setAadhaarConsent(false);
                    return;
                  }
                  setScreen("bankSuccess");
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={submitAadhaar}
                disabled={aadhaarSending}
              >
                {aadhaarProfile
                  ? "Proceed"
                  : aadhaarSending
                    ? "Sending OTP..."
                    : "Proceed"}
              </button>
            </div>
          </section>
        )}

        {screen === "referenceDetails" && (
          <section className="fw-screen fw-screen--form fw-screen--reference fw-fade">
            <h1 className="fw-title">Reference Details</h1>
            <p className="fw-sub fw-reference__sub">
              Please provide verified references for documentation purposes.
            </p>

            <div className="fw-ref-tabs">
              {[0, 1].map((index) => (
                <button
                  key={index}
                  type="button"
                  className={`fw-ref-tab ${activeReferenceTab === index ? "is-active" : ""}`}
                  onClick={() => {
                    setActiveReferenceTab(index);
                    setReferenceError("");
                  }}
                >
                  <FiUser />
                  Reference {index + 1}
                </button>
              ))}
            </div>

            <div className="fw-reference__form">
              <label className="fw-field">
                <span>Reference Name</span>
                <input
                  value={references[activeReferenceTab].name}
                  placeholder="Joey Tribbiani"
                  onChange={(e) => updateReference(activeReferenceTab, "name", e.target.value)}
                />
              </label>

              <label className="fw-field">
                <span>Mobile Number</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={references[activeReferenceTab].mobile}
                  placeholder="eg. 9864437856"
                  onChange={(e) =>
                    updateReference(
                      activeReferenceTab,
                      "mobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                />
              </label>

              <FwDropdown
                id={`relation-${activeReferenceTab}`}
                label="Relation"
                value={references[activeReferenceTab].relation}
                placeholder="Select relation"
                options={REFERENCE_RELATIONS}
                openKey={openDropdown}
                setOpenKey={setOpenDropdown}
                onChange={(val) => updateReference(activeReferenceTab, "relation", val)}
              />

              {referenceError && <p className="fw-error">{referenceError}</p>}
            </div>

            <div className="fw-actions">
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() => {
                  if (activeReferenceTab === 1) {
                    setActiveReferenceTab(0);
                    setReferenceError("");
                    return;
                  }
                  setScreen("aadhaarKyc");
                }}
              >
                Back
              </button>
              <button type="button" className="fw-btn fw-btn--primary" onClick={proceedReference}>
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "referenceReview" && (
          <section className="fw-screen fw-screen--reference-review fw-fade">
            <h1 className="fw-title fw-title--sm">
              Please review and confirm your reference details.
            </h1>

            <div className="fw-reference__scroll">
              {references.map((ref, index) => (
                <div key={`ref-summary-${index}`} className="fw-ref-summary">
                  <h2>Reference {index + 1} Summary</h2>
                  <div className="fw-ref-summary__row">
                    <span>Name</span>
                    <strong>{ref.name}</strong>
                  </div>
                  <div className="fw-ref-summary__row">
                    <span>Mobile Number</span>
                    <strong>{ref.mobile}</strong>
                  </div>
                  <div className="fw-ref-summary__row">
                    <span>Relation</span>
                    <strong>{ref.relation}</strong>
                  </div>
                </div>
              ))}
            </div>

            <div className="fw-actions fw-actions--review">
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() => setScreen("referenceDetails")}
              >
                Edit
              </button>
              <button type="button" className="fw-btn fw-btn--dark" onClick={confirmReferences}>
                Confirm and Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "selfieIntro" && (
          <section className="fw-screen fw-screen--selfie fw-fade">
            <h1 className="fw-title">Take a Selfie</h1>

            <div className="fw-selfie-intro__art" aria-hidden="true">
              <span className="fw-selfie-intro__glow" />
              <svg viewBox="0 0 180 180" fill="none" className="fw-selfie-intro__svg">
                <circle cx="90" cy="90" r="78" fill="#f3f0f8" />
                <circle cx="90" cy="90" r="78" stroke="#e4dff5" strokeWidth="1.5" strokeDasharray="5 7" />
                <ellipse cx="90" cy="74" rx="34" ry="38" fill="#fff" stroke="#c8c4dc" strokeWidth="2.5" />
                <path
                  d="M48 148c10-24 28-36 42-36s32 12 42 36"
                  fill="#fff"
                  stroke="#c8c4dc"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="78" cy="70" r="3" fill="#6b68a6" />
                <circle cx="102" cy="70" r="3" fill="#6b68a6" />
                <path d="M82 86c4 5 12 5 16 0" stroke="#6b68a6" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>

            <div className="fw-selfie-tips">
              <h2>Quick tip for you</h2>
              <div className="fw-selfie-tip">
                <span className="fw-selfie-tip__icon"><FiCamera /></span>
                <p>Allow camera permission to take a selfie to be matched with KYC records.</p>
              </div>
              <div className="fw-selfie-tip">
                <span className="fw-selfie-tip__icon"><FiZap /></span>
                <p>Click a selfie in a well-lit environment. Avoid wearing a cap, glasses or mask.</p>
              </div>
            </div>

            <div className="fw-selfie-safe">
              <strong>100% Safe and Secure</strong>
              <p>The data shared will be encrypted, stored securely, and only used to verify your identity.</p>
            </div>

            <div className="fw-actions">
              <button type="button" className="fw-btn fw-btn--ghost" onClick={() => setScreen("referenceReview")}>
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={() => {
                  setSelfieFaceReady(false);
                  setSelfieError("");
                  setSelfieFaceReady(false);
                  setSelfieError("");
                  setSelfieHint("Opening camera…");
                  setScreen("selfieCapture");
                }}
              >
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "selfieCapture" && (
          <section className="fw-screen fw-screen--selfie fw-fade">
            <h1 className="fw-title">Take a Selfie</h1>

            <div className="fw-selfie-capture">
              <FwSelfieCamera
                ref={selfieCameraRef}
                active={screen === "selfieCapture"}
                onReadyChange={setSelfieFaceReady}
                onHintChange={setSelfieHint}
                onError={setSelfieError}
              />
              <p className="fw-selfie-capture__hint">{selfieHint}</p>
              {selfieError && <p className="fw-error">{selfieError}</p>}
            </div>

            <div className="fw-selfie-safe">
              <strong>100% Safe and Secure</strong>
              <p>The data share will be encrypted, stored securely, and only used to verify your identity.</p>
            </div>

            <div className="fw-actions">
              <button type="button" className="fw-btn fw-btn--ghost" onClick={() => setScreen("selfieIntro")}>
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                disabled={!selfieFaceReady || leadSubmitting}
                onClick={() => {
                  const photo = selfieCameraRef.current?.capturePhoto?.() || "";
                  if (!photo) {
                    setSelfieError("Could not capture selfie. Please try again.");
                    return;
                  }
                  setSelfiePhoto(photo);
                  persistSelfie(photo);
                  submitLeadAndContinue(photo);
                }}
              >
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "leadSubmitting" && (
          <section className="fw-screen fw-screen--plain fw-fade fw-submit-wait">
            <div className="fw-submit-wait__spinner" aria-hidden="true" />
            <h1 className="fw-title">Submitting your application</h1>
            <p className="fw-sub">Please wait while we save your details securely.</p>
          </section>
        )}

        {screen === "leadSubmitFail" && (
          <section className="fw-screen fw-screen--plain fw-fade fw-submit-fail">
            <h1 className="fw-title">Application could not be submitted</h1>
            <p className="fw-error fw-submit-fail__msg">
              {leadSubmitError || "Please try again."}
            </p>
            <div className="fw-actions">
              <button
                type="button"
                className="fw-btn fw-btn--ghost"
                onClick={() => setScreen("selfieCapture")}
              >
                Back
              </button>
              <button
                type="button"
                className="fw-btn fw-btn--primary"
                onClick={() => submitLeadAndContinue()}
                disabled={leadSubmitting}
              >
                {leadSubmitting ? "Submitting..." : "Retry"}
              </button>
            </div>
          </section>
        )}

        {screen === "selfieSuccess" && (
          <section className="fw-screen fw-screen--selfie-success fw-fade">
            <div className="fw-selfie-success">
              <span className="fw-selfie-success__icon"><FiCheck /></span>
              <p>
                Congratulations. Your application has
                <br />
                been submitted
              </p>
            </div>
            <div className="fw-screen__footer">
              <button
                type="button"
                className="fw-btn fw-btn--dark fw-btn--full"
                onClick={() => setScreen("profile")}
              >
                Proceed
              </button>
            </div>
          </section>
        )}

        {screen === "hold" && (
          <section className="fw-screen fw-screen--plain fw-fade fw-hold">
            <div className="fw-brand fw-brand--small">
              <span className="fw-brand__top">FUNDS</span>
              <span className="fw-brand__bottom">WALLE</span>
            </div>
            <h1>You’re almost there</h1>
            <p>{holdMessage}</p>
            <Link to="/" className="fw-btn fw-btn--ghost">Back to Home</Link>
          </section>
        )}

        {screen === "profile" && (
          <FwProfileScreen
            name={
              customerProfile?.name ||
              panProfile?.name ||
              aadhaarProfile?.name ||
              ""
            }
            email={customerProfile?.email || email || ""}
            mobile={customerProfile?.mobile || mobile || ""}
            avatarUrl={selfiePhoto || customerProfile?.avatarUrl || ""}
            loading={profileLoading && !(customerProfile?.name || panProfile?.name || aadhaarProfile?.name)}
            activeTab="profile"
            onEdit={() => {}}
            onMenuClick={(id) => {
              if (id === "transactions") {
                setPaymentHistoryTab("transactions");
                setScreen("paymentHistory");
              }
              if (id === "loans") {
                setPaymentHistoryTab("loans");
                setScreen("paymentHistory");
              }
              if (id === "call") window.location.href = "tel:+918800846929";
              if (id === "email") window.location.href = "mailto:info@fundswalle.com";
              if (id === "about") {
                window.open("https://fundswalle.com/about-us", "_blank", "noopener,noreferrer");
              }
              if (id === "terms") {
                window.open("https://fundswalle.com/terms-conditions", "_blank", "noopener,noreferrer");
              }
              if (id === "privacy") {
                window.open("https://fundswalle.com/privacy-policy", "_blank", "noopener,noreferrer");
              }
            }}
            onTabChange={(tab) => {
              if (tab === "home") setScreen("landing");
              if (tab === "docs") {
                setPaymentHistoryTab("transactions");
                setScreen("paymentHistory");
              }
            }}
            onLogout={() => {
              clearCustomerSession();
              setCustomerProfile(null);
              setMobile("");
              setScreen("landing");
            }}
          />
        )}

        {screen === "paymentHistory" && (
          <FwPaymentHistoryScreen
            activeTab={paymentHistoryTab}
            onTabChange={setPaymentHistoryTab}
            onNavChange={(tab) => {
              if (tab === "home") setScreen("landing");
              if (tab === "profile") setScreen("profile");
            }}
          />
        )}

        {sheet && (
          <div className={`fw-overlay ${sheetVisible ? "is-open" : ""}`}>
            <button type="button" className="fw-overlay__backdrop" aria-label="Close" onClick={closeSheet} />
            <div className={`fw-sheet ${sheetVisible ? "is-open" : ""}`}>
              {sheet === "mobile" && (
                <>
                  <h2>Mobile Verification</h2>
                  <p className="fw-sheet__sub">Enter your mobile number linked to Aadhaar</p>
                  <div className="fw-mobile-input">
                    <span>🇮🇳</span>
                    <strong>+91</strong>
                    <i />
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Enter your mobile number"
                      value={mobile}
                      onChange={(e) => {
                        setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                        setMobileError("");
                      }}
                    />
                  </div>
                  <label className="fw-check">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={() => {
                        setConsent((prev) => !prev);
                        setMobileError("");
                      }}
                    />
                    <span>
                      Allow Fundswalle to access your credit information from{" "}
                      <u>RBI approved bureaus</u>.
                    </span>
                  </label>
                  {mobileError && <p className="fw-error">{mobileError}</p>}
                  <div className="fw-actions">
                    <button type="button" className="fw-btn fw-btn--ghost" onClick={closeSheet}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="fw-btn fw-btn--primary"
                      onClick={sendOtp}
                      disabled={otpSending}
                    >
                      {otpSending ? "Sending..." : "Send OTP"}
                    </button>
                  </div>
                </>
              )}

              {sheet === "otp" && (
                <>
                  <h2>
                    Enter the OTP sent to <b>{formatMobileDisplay(mobile)}</b>
                  </h2>
                  <div className="fw-otp" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          otpRefs.current[index] = el;
                        }}
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        placeholder="0"
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      />
                    ))}
                  </div>
                  <p className="fw-resend">
                    Have not received your OTP?{" "}
                    {resendTimer > 0 ? (
                      <span>Resend in {resendTimer} seconds</span>
                    ) : (
                      <button type="button" onClick={sendOtp} disabled={otpSending}>
                        Resend OTP
                      </button>
                    )}
                  </p>
                  <p className="fw-legal">
                    by proceeding, you are indicating that you have read and agree to our{" "}
                    <Link to="/terms-conditions">terms of use</Link> &{" "}
                    <Link to="/privacy-policy">privacy policy</Link>
                  </p>
                  {otpError && <p className="fw-error">{otpError}</p>}
                  <div className="fw-actions">
                    <button type="button" className="fw-btn fw-btn--ghost" onClick={() => openSheet("mobile")}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="fw-btn fw-btn--primary"
                      onClick={verifyOtp}
                      disabled={otpVerifying || otpValue.length !== 6}
                    >
                      {otpVerifying ? "Verifying..." : "Verify"}
                    </button>
                  </div>
                </>
              )}

              {sheet === "confirm" && panProfile && (
                <div className="fw-confirm">
                  <h2>Is this information correct?</h2>
                  <p className="fw-sheet__sub">Please match this with your PAN card.</p>

                  <div className="fw-pancard">
                    <div className="fw-pancard__head">
                      <div className="fw-pancard__org">
                        <small>आयकर विभाग</small>
                        <b>INCOME TAX DEPARTMENT</b>
                      </div>
                      <img className="fw-pancard__emblem" src={indiaEmblem} alt="" />
                      <div className="fw-pancard__govt">
                        <small>भारत सरकार</small>
                        <b>GOVT. OF INDIA</b>
                      </div>
                    </div>

                    <div className="fw-pancard__body">
                      <div className="fw-pancard__grid">
                        <p>
                          <span>Name</span>
                          <strong>{panProfile.name}</strong>
                        </p>
                        <p>
                          <span>Father’s Name</span>
                          <strong>{panProfile.fatherName || "—"}</strong>
                        </p>
                        <p>
                          <span>Date of Birth</span>
                          <strong>{panProfile.dob}</strong>
                        </p>
                        <p>
                          <span>PAN Number</span>
                          <strong>{panProfile.pan}</strong>
                        </p>
                      </div>
                      <div className="fw-pancard__sign">
                        <i>{(panProfile.name || "").split(" ")[0]}</i>
                        <span>Signature</span>
                      </div>
                    </div>
                  </div>

                  <div className="fw-actions fw-actions--compact">
                    <button type="button" className="fw-btn fw-btn--ghost" onClick={closeSheet}>
                      No
                    </button>
                    <button type="button" className="fw-btn fw-btn--dark" onClick={confirmAndProceed}>
                      Confirm
                    </button>
                  </div>
                </div>
              )}

              {sheet === "aaOtp" && (
                <>
                  <h2>
                    OTP sent to <b>{formatMobileDisplay(aaMobile)}</b>
                  </h2>
                  <div className="fw-otp">
                    {aaOtpDigits.map((digit, index) => (
                      <input
                        key={`aa-${index}`}
                        ref={(el) => {
                          aaOtpRefs.current[index] = el;
                        }}
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        placeholder="0"
                        onChange={(e) => handleAaOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleAaOtpKeyDown(index, e)}
                      />
                    ))}
                  </div>
                  <p className="fw-resend">
                    Have not received your OTP?{" "}
                    {aaResendTimer > 0 ? (
                      <span>Resend in {aaResendTimer} seconds</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setAaResendTimer(OTP_RESEND_SECONDS);
                          setAaOtpDigits(["", "", "", "", "", ""]);
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </p>
                  <p className="fw-legal">
                    By proceeding, you accept Fundswalle Terms &amp; Conditions. For support,
                    please contact Fundswalle.
                  </p>
                  {aaOtpError && <p className="fw-error">{aaOtpError}</p>}
                  <div className="fw-actions">
                    <button type="button" className="fw-btn fw-btn--ghost" onClick={closeSheet}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="fw-btn fw-btn--primary"
                      onClick={verifyAaOtp}
                      disabled={aaOtpValue.length !== 6}
                    >
                      Verify
                    </button>
                  </div>
                </>
              )}

              {sheet === "aadhaarOtp" && (
                <>
                  <h2>Enter Aadhaar OTP</h2>
                  <p className="fw-sheet__sub">
                    OTP sent to the mobile number linked with Aadhaar{" "}
                    <b>{formatAadhaarDisplay(aadhaarNumber)}</b>
                  </p>
                  <div className="fw-otp">
                    {aadhaarOtpDigits.map((digit, index) => (
                      <input
                        key={`aadhaar-otp-${index}`}
                        ref={(el) => {
                          aadhaarOtpRefs.current[index] = el;
                        }}
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        placeholder="0"
                        onChange={(e) => handleAadhaarOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleAadhaarOtpKeyDown(index, e)}
                      />
                    ))}
                  </div>
                  <p className="fw-resend">
                    Have not received your OTP?{" "}
                    {aadhaarResendTimer > 0 ? (
                      <span>Resend in {aadhaarResendTimer} seconds</span>
                    ) : (
                      <button
                        type="button"
                        onClick={resendAadhaarOtp}
                        disabled={aadhaarSending}
                      >
                        {aadhaarSending ? "Sending..." : "Resend OTP"}
                      </button>
                    )}
                  </p>
                  {aadhaarOtpError && <p className="fw-error">{aadhaarOtpError}</p>}
                  <div className="fw-actions">
                    <button type="button" className="fw-btn fw-btn--ghost" onClick={closeSheet}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="fw-btn fw-btn--primary"
                      onClick={verifyAadhaarOtp}
                      disabled={aadhaarOtpVerifying || aadhaarOtpDigits.join("").length !== 6}
                    >
                      {aadhaarOtpVerifying ? "Verifying..." : "Verify"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplyNewFlow;
